import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  FileText, 
  Download, 
  Mail, 
  Trash2, 
  Edit2, 
  Edit,
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  Printer,
  Loader2,
  X,
  ChevronDown,
  Calendar as CalendarIcon,
  User,
  History,
  Check,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  Tag as TagIcon,
  PackageCheck,
  Store,
  MessageSquare as CommentIcon,
  Link as LinkIcon,
  QrCode
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { configurePdfUnicodeFont, PDF_UNICODE_FONT } from '../lib/pdfFont';
import { localizeJsPdfInstance, localizePdfText } from '../lib/localization';
import { cn } from '../lib/utils';
import { getDefaultCurrency } from '../lib/currency';
import { CustomSelect } from './ui/CustomSelect';
import { SearchableSelect } from './ui/SearchableSelect';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc,
  getDoc,
  deleteDoc,
  arrayUnion,
  increment } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from '../firebase';
import { db, auth, storage, handleFirestoreError, OperationType } from '../firebase';
import { DocumentPrintLayout } from './ui/DocumentPrintLayout';
import { StatusBadge } from './workflow/StatusBadge';
import type { StatusBadgeValue } from '../lib/documentStatus';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ConfirmModal from './ConfirmModal';
import ShareDocumentModal from './ShareDocumentModal';
import DiscussionsView from './DiscussionsView';
import DocumentChatterPanel from './DocumentChatterPanel';
import { logHistory } from '../services/historyService';
import { notify } from '../services/notify';
import { createLocalNotification } from '../services/notificationService';

interface ActionLogEntry {
  action: string;
  user?: string;
  email?: string;
  timestamp?: string;
  by?: string;
  byEmail?: string;
  at?: any;
  details: string;
  relatedId?: string;
  relatedType?: 'bill' | 'invoice' | 'purchase_order';
  actionLink?: string;
  actionType?: string;
}

interface LandedCostLine {
  id: string;
  expenseName: string;
  amount: number;
}

interface PurchaseOrderItem {
  id?: string;
  itemId?: string;
  item: string;
  description: string;
  quantity: number;
  receivedQuantity?: number;
  billedQuantity?: number;
  unitPrice: number;
  discount: number;
  discountType?: 'percentage' | 'amount';
  taxAmount: number;
  total: number;
  account: string;
  taxRate: string;
  trackingCategory?: string;
  weight?: number;
  allocatedLandedCost?: number;
  weightedUnitCost?: number;
  lotNumber?: string;
  expiryDate?: string;
  uomId?: string;
  uomName?: string;
  uomCategory?: string;
}

interface POReply {
  id: string;
  text: string;
  userId: string;
  userName: string;
  createdAt: string;
}

interface POThread {
  id: string;
  title: string;
  userId: string;
  userName: string;
  createdAt: string;
  replies: POReply[];
}

interface PurchaseOrder {
  id: string;
  number: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  date: string;
  deliveryDate: string;
  reference: string;
  currency: string;
  brandingTheme: string;
  amountsAre: 'Tax Inclusive' | 'Tax Exclusive' | 'No Tax';
  items: PurchaseOrderItem[];
  totalAmount: number;
  status: 'Draft' | 'Sent' | 'Accepted' | 'Rejected' | 'Awaiting Approval' | 'Approved' | 'Billed' | 'Converted' | 'Void' | 'Deleted' | 'Received';
  isReceived?: boolean;
  receivedAt?: any;
  receivedBy?: string;
  deliveryInstructions: string;
  deliveryAddress: string;
  billingAddress?: string;
  attachments?: string[];
  actionLog?: ActionLogEntry[];
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  actionDetails?: string;
  source?: string;
  tags?: string[];
  purchaseOrderId?: string;
  billId?: string;
  notes?: string;
  type: 'purchase_order';
  threads?: POThread[];
  landedCostLines?: LandedCostLine[];
  landedCostMethod?: 'value' | 'quantity' | 'weight';
  landedCostTotal?: number;
}

interface Contact {
  id: string;
  name: string;
  email?: string;
  emails?: string[];
  phone?: string;
  type?: 'Customer' | 'Supplier' | 'Both';
  address?: string;
  billingAddress?: string;
  deliveryAddress?: string;
  purchaseAccount?: string;
  purchaseTaxRate?: string;
  purchasePaymentTerms?: string;
  currency?: string;
  purchaseCurrency?: string;
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
}

// Removed local handleFirestoreError and OperationType as they are imported from firebase.ts

const hasLocalSession = () =>
  typeof window !== 'undefined' && Boolean(localStorage.getItem('app_auth_token'));

// Local settings come from the `settings` DB row (snake_cased columns), while the
// Firestore settings doc uses camelCase. Reads need to tolerate both shapes.
const readNextPurchaseOrderNumber = (settings: any): number => {
  if (!settings) return 1000;
  const raw = settings.nextPurchaseOrderNumber ?? settings.next_purchase_order_number;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1000;
};

const readPurchaseOrderPrefix = (settings: any): string => {
  const prefix = settings?.purchaseOrderPrefix ?? settings?.purchase_order_prefix;
  return typeof prefix === 'string' && prefix.trim() ? prefix : 'PO-';
};

const buildPurchaseOrderNumber = (settings: any): string =>
  `${readPurchaseOrderPrefix(settings)}${readNextPurchaseOrderNumber(settings)}`;

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('app_auth_token');
  if (!token) throw new Error('Local session token is unavailable');

  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw new Error((await response.text()) || `Request failed (${response.status})`);
  }

  return response.status === 204 ? (undefined as T) : response.json();
};

const localApiRecords = <T,>(payload: any): T[] =>
  Array.isArray(payload) ? payload : payload?.documents || payload?.contacts || payload?.items ||
  payload?.accounts || payload?.chartOfAccounts || payload?.taxRates || payload?.tax_rates || payload?.data || [];

const localApiRecord = <T,>(payload: any): T =>
  (payload?.document || payload?.data || payload) as T;

// Older records may contain an accidentally persisted select event instead of the vendor name.
const normalizeContactName = (value: unknown): string =>
  typeof value === 'string' ? value : '';

// Canonical purchase-order statuses used by the UI. The seeded/local documents
// store lowercase strings (e.g. 'draft', 'approved'), while every status check
// in this view compares against the capitalized form below. Normalizing once on
// load keeps the UI consistent regardless of how the value was persisted.
const PURCHASE_ORDER_STATUSES = [
  'Draft',
  'Sent',
  'Accepted',
  'Rejected',
  'Awaiting Approval',
  'Approved',
  'Billed',
  'Converted',
  'Void',
  'Deleted',
  'Received',
] as const;

type CanonicalPurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

const normalizePurchaseOrderStatus = (value: unknown): CanonicalPurchaseOrderStatus => {
  if (typeof value !== 'string' || !value) return 'Draft';
  // Exact (already canonical) match.
  if ((PURCHASE_ORDER_STATUSES as readonly string[]).includes(value)) {
    return value as CanonicalPurchaseOrderStatus;
  }
  const lower = value.toLowerCase();
  const match = (PURCHASE_ORDER_STATUSES as readonly string[]).find(
    (status) => status.toLowerCase() === lower,
  );
  if (match) return match as CanonicalPurchaseOrderStatus;
  // Map common variants used historically by Firestore imports.
  if (lower === 'deleted' || lower === 'void' || lower === 'cancelled' || lower === 'canceled') return 'Deleted';
  if (lower === 'paid' || lower === 'billed') return 'Billed';
  if (lower === 'awaitingapproval' || lower === 'awaiting_approval') return 'Awaiting Approval';
  return value.charAt(0).toUpperCase() + value.slice(1) as CanonicalPurchaseOrderStatus;
};

const normalizePurchaseOrder = (po: PurchaseOrder): PurchaseOrder => ({
  ...po,
  contactName: normalizeContactName(po.contactName),
  status: normalizePurchaseOrderStatus(po.status),
});

const purchaseOrderBadgeStatus = (status: PurchaseOrder['status']): StatusBadgeValue => {
  if (status === 'Draft') return 'draft';
  if (status === 'Sent') return 'sent';
  if (status === 'Accepted') return 'accepted';
  if (status === 'Rejected') return 'declined';
  if (status === 'Awaiting Approval') return 'pending';
  if (status === 'Void' || status === 'Deleted') return 'cancelled';
  return 'done';
};

const isPurchaseOrderValueLocked = (status: PurchaseOrder['status']) =>
  ['Approved', 'Received', 'Billed', 'Converted', 'Void', 'Deleted'].includes(status);

const purchaseUomOptions = (product: any) => {
  if (!product) return [];
  const roles = ['purchase', 'stock', 'sales'] as const;
  const seen = new Set<string>();
  return roles.flatMap(role => {
    const id = product[`${role}UomId`];
    const name = product[`${role}UomName`];
    const category = product[`${role}UomCategory`];
    const key = id || name;
    if (!key || seen.has(key)) return [];
    seen.add(key);
    return [{ id: String(id || name), name: String(name || id), category: String(category || '') }];
  });
};

interface PurchaseOrdersViewProps {
  onMarkAsBilled?: () => void;
}

export default function PurchaseOrdersView({  onMarkAsBilled, orgId, onNavigate, initialPurchaseOrderId, onClearInitial, language = 'en' }: {  onMarkAsBilled?: () => void, orgId: string, onNavigate?: (tab: string, id?: string) => void, initialPurchaseOrderId?: string | null, onClearInitial?: () => void, language?: string }) {
  const [currencies, setCurrencies] = useState(['USD', 'EGP', 'EUR', 'GBP', 'AUD', 'CAD']);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [docToPrint, setDocToPrint] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    if (!orgId || hasLocalSession()) return;
    const fetchOrg = async () => {
      const docSnap = await getDoc(doc(db, 'organizations', orgId));
      if (docSnap.exists()) {
        setOrganization({ id: docSnap.id, ...docSnap.data() });
      }
    };
    fetchOrg();
  }, [orgId]);

  const handlePrint = (doc: any) => {
    setDocToPrint(doc);
    setIsPrintModalOpen(true);
  };
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('default');
  const [lotNumbers, setLotNumbers] = useState<any[]>([]);
  const [selectedLotNumber, setSelectedLotNumber] = useState<string>('');
  const [selectedExpiryDate, setSelectedExpiryDate] = useState<string>('');
  const [showThread, setShowThread] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const handleSelectPO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    onNavigate?.('purchase_orders', po.id);
  };
  const handleClosePO = () => {
    setSelectedPO(null);
    onNavigate?.('purchase_orders');
  };
  const [selectedPOs, setSelectedPOs] = useState<string[]>([]);
  const [shareMode, setShareMode] = useState<'link' | 'qr' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refFilter, setRefFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newReply, setNewReply] = useState('');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Landed Cost Modal State
  const [isLandedCostModalOpen, setIsLandedCostModalOpen] = useState(false);
  const [landedCostMethod, setLandedCostMethod] = useState<'value' | 'quantity' | 'weight'>('value');
  const [landedCostLines, setLandedCostLines] = useState<LandedCostLine[]>([]);
  const [previewAllocations, setPreviewAllocations] = useState<PurchaseOrderItem[] | null>(null);
  const [landedCost, setLandedCost] = useState('');

  // Partial Bill state
  const [isPartialBillModalOpen, setIsPartialBillModalOpen] = useState(false);
  const [partialBillItems, setPartialBillItems] = useState<any[]>([]);
  const [isPartialSaving, setIsPartialSaving] = useState(false);

  const getTaxRateValue = (rateName: string) => {
    if (!rateName) return 0;
    if (rateName.endsWith('%')) {
      return parseFloat(rateName) || 0;
    }
    const rateObj = taxRates.find(r => r.name === rateName);
    return rateObj ? parseFloat(rateObj.rate) || 0 : 0;
  };

  const calculateLandedCosts = () => {
    if (!selectedPO) return;
    
    // Safety check for empty lines or negative expenses
    const validLines = landedCostLines.filter(l => l.expenseName.trim() !== '' && l.amount > 0);
    const totalExpense = validLines.reduce((sum, line) => sum + line.amount, 0);

    const items = [...selectedPO.items];
    const totalValue = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalWeight = items.reduce((sum, item) => sum + ((item.weight || 1) * item.quantity), 0);

    let distributedTotal = 0;
    const allocations: number[] = [];

    items.forEach((item) => {
      let ratio = 0;
      if (landedCostMethod === 'value' && totalValue > 0) {
        ratio = (item.unitPrice * item.quantity) / totalValue;
      } else if (landedCostMethod === 'quantity' && totalQty > 0) {
        ratio = item.quantity / totalQty;
      } else if (landedCostMethod === 'weight' && totalWeight > 0) {
        ratio = ((item.weight || 1) * item.quantity) / totalWeight;
      }

      const allocated = Math.round(ratio * totalExpense * 100) / 100;
      allocations.push(allocated);
      distributedTotal += allocated;
    });

    // Handle rounding variance mathematically
    const difference = Math.round((totalExpense - distributedTotal) * 100) / 100;
    if (difference !== 0 && allocations.length > 0) {
      allocations[0] = Math.round((allocations[0] + difference) * 100) / 100;
    }

    const calculatedItems = items.map((item, index) => {
      const allocatedCost = allocations[index];
      const newTotalCost = (item.unitPrice * item.quantity) + allocatedCost;
      const newUnitCost = newTotalCost / (item.quantity || 1); // Protect against zero division
      
      return {
        ...item,
        allocatedLandedCost: allocatedCost,
        weightedUnitCost: newUnitCost
      };
    });

    setPreviewAllocations(calculatedItems);
  };

  const handleApplyLandedCosts = async () => {
    if (!selectedPO || !previewAllocations) return;
    
    try {
      const validLines = landedCostLines.filter(l => l.expenseName.trim() !== '' && l.amount > 0);
      const totalExpense = validLines.reduce((sum, line) => sum + line.amount, 0);

      await updateDoc(doc(db, 'documents', selectedPO.id), {
        items: previewAllocations,
        landedCostLines: validLines,
        landedCostMethod,
        landedCostTotal: totalExpense,
        updatedAt: serverTimestamp()
      });

      setSelectedPO(prev => prev ? {
        ...prev, 
        items: previewAllocations,
        landedCostLines: validLines,
        landedCostMethod,
        landedCostTotal: totalExpense
      } : null);

      notify.confirm('Landed costs successfully allocated!');
      setIsLandedCostModalOpen(false);
      setPreviewAllocations(null);
    } catch (err) {
      console.error(err);
      notify.error('Failed to apply landed costs');
    }
  };

  const handleCreateThread = async (poId: string) => {
    if (!newThreadTitle.trim()) return;
    try {
      const po = purchaseOrders.find(p => p.id === poId);
      if (!po) return;

      const thread: POThread = {
        id: Math.random().toString(36).substr(2, 9),
        title: newThreadTitle,
        userId: auth.currentUser?.uid || 'unknown',
        userName: auth.currentUser?.displayName || 'Unknown User',
        createdAt: new Date().toISOString(),
        replies: []
      };

      await updateDoc(doc(db, 'documents', poId), {
        threads: arrayUnion(thread),
        updatedAt: serverTimestamp()
      });

      const threadTitle = newThreadTitle;
      setNewThreadTitle('');
      
      if (po.createdBy && po.createdBy !== auth.currentUser?.uid) {
        if (hasLocalSession()) {
          await createLocalNotification(orgId, {
            recipientUserId: po.createdBy,
            title: 'New Discussion Thread on Purchase Order',
            message: `${auth.currentUser?.displayName || 'Someone'} started a new thread on PO ${po.number}: "${threadTitle}"`,
            type: 'po_comment',
            relatedId: poId,
          });
        } else {
          await addDoc(collection(db, 'notifications'), {
            userId: po.createdBy,
            orgId,
            title: 'New Discussion Thread on Purchase Order',
            message: `${auth.currentUser?.displayName || 'Someone'} started a new thread on PO ${po.number}: "${threadTitle}"`,
            type: 'po_comment',
            relatedId: poId,
            read: false,
            createdAt: serverTimestamp()
          });
        }
      }
      notify.confirm("Thread started");
    } catch (error) {
      console.error("Error creating thread:", error);
      notify.error("Failed to start thread");
    }
  };

  const handleReplyToThread = async (poId: string, threadId: string) => {
    if (!newReply.trim()) return;
    try {
      const po = purchaseOrders.find(p => p.id === poId);
      if (!po) return;

      const reply: POReply = {
        id: Math.random().toString(36).substr(2, 9),
        text: newReply,
        userId: auth.currentUser?.uid || 'unknown',
        userName: auth.currentUser?.displayName || 'Unknown User',
        createdAt: new Date().toISOString()
      };

      const updatedThreads = (po.threads || []).map(thread => {
        if (thread.id === threadId) {
          return {
            ...thread,
            replies: [...(thread.replies || []), reply]
          };
        }
        return thread;
      });

      await updateDoc(doc(db, 'documents', poId), {
        threads: updatedThreads,
        updatedAt: serverTimestamp()
      });

      setNewReply('');
      
      if (po.createdBy && po.createdBy !== auth.currentUser?.uid) {
        if (hasLocalSession()) {
          await createLocalNotification(orgId, {
            recipientUserId: po.createdBy,
            title: 'New Reply on PO Discussion',
            message: `${auth.currentUser?.displayName || 'Someone'} replied in a discussion on PO ${po.number}`,
            type: 'po_comment',
            relatedId: poId,
          });
        } else {
          await addDoc(collection(db, 'notifications'), {
            userId: po.createdBy,
            orgId,
            title: 'New Reply on PO Discussion',
            message: `${auth.currentUser?.displayName || 'Someone'} replied in a discussion on PO ${po.number}`,
            type: 'po_comment',
            relatedId: poId,
            read: false,
            createdAt: serverTimestamp()
          });
        }
      }
      notify.confirm("Reply posted");
    } catch (error) {
      console.error("Error posting reply:", error);
      notify.error("Failed to post reply");
    }
  };

  const handleBulkAction = async (action: 'submit' | 'approve' | 'delete' | 'print' | 'email') => {
    if (selectedPOs.length === 0) return;

    try {
      if (action === 'delete') {
        for (const id of selectedPOs) {
          await updateDoc(doc(db, 'documents', id), { status: 'Deleted' });
        }
        notify.confirm(`${selectedPOs.length} purchase orders deleted`);
      } else if (action === 'approve') {
        for (const id of selectedPOs) {
          await updateDoc(doc(db, 'documents', id), { status: 'Approved' });
        }
        notify.confirm(`${selectedPOs.length} purchase orders approved`);
      } else if (action === 'submit') {
        for (const id of selectedPOs) {
          await updateDoc(doc(db, 'documents', id), { status: 'Awaiting Approval' });
        }
        notify.confirm(`${selectedPOs.length} purchase orders submitted for approval`);
      } else if (action === 'print') {
        notify.confirm("Printing functionality for multiple purchase orders is being prepared.");
      } else if (action === 'email') {
        notify.confirm("Email functionality for multiple purchase orders is being prepared.");
      }
      
      setSelectedPOs([]);
    } catch (error) {
      console.error("Error performing bulk action:", error);
      notify.error("Failed to perform bulk action");
    }
  };
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const organizationCurrency = getDefaultCurrency(orgSettings);
  const [itemsList, setItemsList] = useState<any[]>([]);
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);
  const [brandingThemes, setBrandingThemes] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [isNewSupplierModalOpen, setIsNewSupplierModalOpen] = useState(false);
  const [isCreatingSupplier, setIsCreatingSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', email: '', phone: '', address: '' });

  const [newPO, setNewPO] = useState<Omit<PurchaseOrder, 'id'> & { trackingCategory?: string; contactEmail?: string }>({
    number: '',
    contactId: '',
    date: new Date().toISOString().split('T')[0],
    deliveryDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reference: '',
    currency: organizationCurrency,
    brandingTheme: 'Standard',
    amountsAre: 'Tax Exclusive',
    items: [{ 
      item: '', 
      description: '', 
      quantity: 1, 
      unitPrice: 0,
      discount: 0,
      taxAmount: 0,
      total: 0,
      account: '',
      taxRate: '0%',
      trackingCategory: ''
    }],
    totalAmount: 0,
    status: 'Draft',
    type: 'purchase_order',
    notes: '',
    deliveryInstructions: '',
    deliveryAddress: '',
    billingAddress: '',
    attachments: [],
    trackingCategory: '',
    tags: []
  });

  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');

  useEffect(() => {
    if (initialPurchaseOrderId) {
      if (initialPurchaseOrderId.startsWith('new-po-for:')) {
        const contactId = initialPurchaseOrderId.split(':')[1];
        setIsEditing(false);
        setNewPO({
          number: '',
          contactId: contactId,
          date: new Date().toISOString().split('T')[0],
          deliveryDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: organizationCurrency,
          brandingTheme: 'Standard',
          amountsAre: 'Tax Exclusive',
          items: [],
          totalAmount: 0,
          status: 'Draft',
          type: 'purchase_order',
          notes: '',
          deliveryInstructions: '',
          deliveryAddress: '',
          billingAddress: '',
          attachments: [],
          trackingCategory: '',
          tags: []
        });
        setIsModalOpen(true);
        if (onClearInitial) onClearInitial();
      } else if (purchaseOrders.length > 0) {
        const po = purchaseOrders.find(p => p.id === initialPurchaseOrderId);
        if (po) {
          setSelectedPO(po);
          if (onClearInitial) onClearInitial();
        }
      }
    }
  }, [initialPurchaseOrderId, purchaseOrders, organizationCurrency]);

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-po') {
        setIsEditing(false);
        setNewPO({
          number: '',
          contactId: '',
          date: new Date().toISOString().split('T')[0],
          deliveryDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: organizationCurrency,
          brandingTheme: 'Standard',
          amountsAre: 'Tax Exclusive',
          items: [],
          totalAmount: 0,
          status: 'Draft',
          type: 'purchase_order',
          notes: '',
          deliveryInstructions: '',
          deliveryAddress: '',
          billingAddress: '',
          attachments: [],
          trackingCategory: '',
          tags: []
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, [organizationCurrency]);

  useEffect(() => {
    if (!orgId) return;

    if (hasLocalSession()) {
      let cancelled = false;
      const loadLocalPurchaseOrderData = async () => {
        setIsLoading(true);
        try {
          const [documentsResponse, contactsResponse, itemsResponse, accountsResponse, taxRatesResponse, settingsResponse] = await Promise.all([
            localApiRequest(`/api/organizations/${orgId}/documents?types=purchase_order`),
            localApiRequest(`/api/organizations/${orgId}/contacts`),
            localApiRequest(`/api/organizations/${orgId}/items`),
            localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`),
            localApiRequest(`/api/organizations/${orgId}/tax-rates`),
            localApiRequest(`/api/organizations/${orgId}/settings`),
          ]);
          if (cancelled) return;

          setPurchaseOrders(localApiRecords<PurchaseOrder>(documentsResponse).map(normalizePurchaseOrder));
          setContacts(localApiRecords<Contact>(contactsResponse).filter(contact => !contact.type || contact.type === 'Supplier' || contact.type === 'Both'));
          setItemsList(localApiRecords<any>(itemsResponse));
          setAccounts(localApiRecords<Account>(accountsResponse));
          setTaxRates(localApiRecords<any>(taxRatesResponse));
                    const settings = localApiRecord<any>(settingsResponse);
                    setOrgSettings(settings);
                    const allowedCurrencies = settings?.allowedCurrencies ?? settings?.allowed_currencies ?? [];
                    setCurrencies(allowedCurrencies.length
                      ? [...new Set([getDefaultCurrency(settings), ...allowedCurrencies])]
                      : [getDefaultCurrency(settings)]);
                    if (!isEditing) {
                      setNewPO(prev => ({ ...prev, currency: getDefaultCurrency(settings), number: buildPurchaseOrderNumber(settings) }));
                    }
        } catch (error) {
          console.error('Local purchase order data load failed:', error);
          notify.error('Failed to load purchase orders');
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      loadLocalPurchaseOrderData();
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'documents'), 
      where('type', '==', 'purchase_order'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PurchaseOrder[];
      setPurchaseOrders(data.map(normalizePurchaseOrder));
      setIsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'documents');
      setIsLoading(false);
    });

    const contactsQ = query(
      collection(db, 'contacts'), 
      where('orgId', '==', orgId)
    );
    const unsubContacts = onSnapshot(contactsQ, (snapshot) => {
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contact)));
    });

    const accountsQ = query(
      collection(db, 'chart_of_accounts'), 
      where('orgId', '==', orgId)
    );
    const unsubAccounts = onSnapshot(accountsQ, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
    });

    const lotQ = query(
      collection(db, 'lot_numbers'),
      where('orgId', '==', orgId)
    );
    const unsubLots = onSnapshot(lotQ, (snapshot) => {
      setLotNumbers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'lot_numbers'));

    const itemsQ = query(
      collection(db, 'items'), 
      where('orgId', '==', orgId)
    );
    const unsubItems = onSnapshot(itemsQ, (snapshot) => {
      setItemsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const trackingQ = query(
      collection(db, 'tracking_categories'), 
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubTracking = onSnapshot(trackingQ, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const themesQ = query(
      collection(db, 'branding_themes'),
      where('orgId', '==', orgId)
    );
    const unsubThemes = onSnapshot(themesQ, (snapshot) => {
      setBrandingThemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'branding_themes');
    });

    const taxRatesQ = query(
      collection(db, 'tax_rates'),
      where('orgId', '==', orgId)
    );
    const unsubTaxRates = onSnapshot(taxRatesQ, (snapshot) => {
      setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const storesQ = query(
      collection(db, 'stores'),
      where('orgId', '==', orgId)
    );
    const unsubStores = onSnapshot(storesQ, (snapshot) => {
      const storesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStores(storesData);
      if (storesData.length > 0) {
        setSelectedWarehouseId(storesData[0].id);
      }
    });

    const warehousesQ = query(
      collection(db, 'warehouses'),
      where('orgId', '==', orgId)
    );
    const unsubWarehouses = onSnapshot(warehousesQ, (snapshot) => {
      const warehousesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWarehouses(warehousesData);
      // If we already have a store selected, we don't necessarily want to override it with a warehouse
      // unless stores is empty.
    });

    // Fetch Tags
    const tagsQ = query(
      collection(db, 'tags'),
      where('orgId', '==', orgId),
      where('type', 'in', ['purchases', null]),
      orderBy('createdAt', 'desc')
    );
    const unsubTags = onSnapshot(tagsQ, (snapshot) => {
      setAvailableTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tags');
    });

    // Fetch next PO number
    const unsubSettings = onSnapshot(doc(db, 'settings', orgId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setOrgSettings(data);
        
        if (data.allowedCurrencies && data.allowedCurrencies.length > 0) {
          const allCurrencies = [...new Set([getDefaultCurrency(data), ...data.allowedCurrencies])];
          setCurrencies(allCurrencies);
        } else if (data.defaultCurrency) {
          setCurrencies([data.defaultCurrency]);
        }

        if (!isEditing) {
          setNewPO(prev => ({ 
            ...prev, 
            number: buildPurchaseOrderNumber(data),
            currency: getDefaultCurrency(data)
          }));
        }
      }
    });

    return () => {
      unsubscribe();
      unsubContacts();
      unsubAccounts();
      unsubSettings();
      unsubItems();
      unsubTracking();
      unsubThemes();
      unsubTaxRates();
      unsubStores();
      unsubWarehouses();
      unsubTags();
      if (typeof unsubLots === 'function') unsubLots();
    };
  }, [orgId, isEditing]);

  const handleLotChange = (lotNo: string) => {
    setSelectedLotNumber(lotNo);
    const lot = lotNumbers.find(l => l.lotNumber === lotNo);
    if (lot && lot.expiryDate) {
      setSelectedExpiryDate(lot.expiryDate);
    }
  };

  const calculateTotals = (items: PurchaseOrderItem[]) => {
    return items.reduce((acc, item) => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = item.discountType === 'amount' 
        ? item.discount 
        : (subtotal * item.discount) / 100;
      const taxableAmount = subtotal - discountAmount;
      const taxRateValue = getTaxRateValue(item.taxRate) / 100;
      const taxAmount = taxableAmount * taxRateValue;
      return acc + taxableAmount + taxAmount;
    }, 0);
  };

  const handleAddItem = () => {
    setNewPO({
      ...newPO,
      items: [...newPO.items, { 
        item: '', 
        description: '', 
        quantity: 1, 
        unitPrice: 0,
        discount: 0,
        discountType: 'percentage',
        taxAmount: 0,
        total: 0,
        account: '',
        taxRate: '0%'
      }]
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = newPO.items.filter((_, i) => i !== index);
    setNewPO({ ...newPO, items: newItems });
  };

  const handleItemChange = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    const newItems = [...newPO.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate item total
    const item = newItems[index];
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = item.discountType === 'amount'
      ? item.discount
      : (subtotal * item.discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxRateValue = getTaxRateValue(item.taxRate) / 100;
    const taxAmount = taxableAmount * taxRateValue;
    newItems[index].total = taxableAmount + taxAmount;
    newItems[index].taxAmount = taxAmount;

    setNewPO({ ...newPO, items: newItems, totalAmount: calculateTotals(newItems) });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (hasLocalSession()) {
      notify.confirm('Attachments are not available in local mode.');
      return;
    }

    setIsSaving(true);
    try {
      const storageRef = ref(storage, `attachments/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setNewPO(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), downloadURL]
      }));
      notify.confirm("File uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      notify.error("Failed to upload file");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newSupplier.name.trim();
    if (!name) return;

    setIsCreatingSupplier(true);
    try {
      const contactData = {
        name,
        email: newSupplier.email.trim(),
        emails: newSupplier.email.trim() ? [newSupplier.email.trim()] : [],
        phone: newSupplier.phone.trim(),
        phones: newSupplier.phone.trim() ? [{ number: newSupplier.phone.trim(), countryCode: '+1' }] : [],
        address: newSupplier.address.trim(),
        billingAddress: newSupplier.address.trim(),
        deliveryAddress: newSupplier.address.trim(),
        type: 'Supplier' as const,
        status: 'Active',
        currency: newPO.currency,
        purchaseCurrency: newPO.currency,
        purchasePaymentTerms: 'Net 30',
        createdBy: auth.currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      let savedContact: Contact;
      if (hasLocalSession()) {
        const response = await localApiRequest(`/api/organizations/${orgId}/contacts`, {
          method: 'POST',
          body: JSON.stringify(contactData),
        });
        savedContact = localApiRecord<Contact>(response);
      } else {
        const contactRef = await addDoc(collection(db, 'contacts'), { ...contactData, orgId });
        savedContact = { ...contactData, id: contactRef.id } as Contact;
      }

      setContacts(current => [...current.filter(contact => contact.id !== savedContact.id), savedContact].sort((a, b) => a.name.localeCompare(b.name)));
      handleContactChange(savedContact.id, savedContact);
      setNewSupplier({ name: '', email: '', phone: '', address: '' });
      setIsNewSupplierModalOpen(false);
      notify.confirm(language === 'ar' ? 'تم إنشاء المورد' : 'Supplier created');
    } catch (error) {
      console.error('Error creating supplier:', error);
      notify.error(language === 'ar' ? 'تعذر إنشاء المورد' : 'Failed to create supplier');
    } finally {
      setIsCreatingSupplier(false);
    }
  };

  const handleContactChange = (contactId: string, contactOverride?: Contact) => {
    const contact = contactOverride || contacts.find(c => c.id === contactId);
    if (contact) {
      setNewPO(prev => ({
        ...prev,
        contactId,
        contactName: contact.name || '',
        contactEmail: contact.email || '',
        currency: organizationCurrency,
        billingAddress: contact.billingAddress || contact.address || '',
        deliveryAddress: contact.deliveryAddress || contact.address || '',
        deliveryInstructions: contact.purchasePaymentTerms ? `Payment Terms: ${contact.purchasePaymentTerms}` : prev.deliveryInstructions,
        items: prev.items.map((item, index) => index === 0 ? {
          ...item,
          account: contact.purchaseAccount || item.account,
          taxRate: contact.purchaseTaxRate || item.taxRate
        } : item)
      }));
    } else {
      setNewPO(prev => ({ ...prev, contactId }));
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedPO) return;

    try {
      const doc = localizeJsPdfInstance(new jsPDF({ unit: 'mm', format: 'a4' }));
      await configurePdfUnicodeFont(doc);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;
      const supplier = contacts.find(c => c.id === selectedPO.contactId);
      const locale = language === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB';
      const formatDate = (value?: string) => value ? new Date(value).toLocaleDateString(locale, {
        day: '2-digit', month: 'short', year: 'numeric'
      }) : '-';
      const money = (value: unknown) => `${selectedPO.currency || ''} ${Number(value || 0).toLocaleString(locale, {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      })}`.trim();
      const text = (value: unknown, fallback = ''): string => value === null || value === undefined ? fallback : String(value);
      const isArabic = language === 'ar';
      const align = isArabic ? 'right' : 'left';
      const rightAlign = isArabic ? 'left' : 'right';
      const labels = isArabic ? {
        title: 'أمر شراء', number: 'الرقم', date: 'التاريخ', delivery: 'تاريخ التسليم',
        supplier: 'المورد', billTo: 'عنوان الفوترة', shipTo: 'عنوان التسليم', reference: 'المرجع',
        description: 'الوصف', lot: 'رقم الدفعة', qty: 'الكمية', unitPrice: 'سعر الوحدة',
        discount: 'الخصم', total: 'الإجمالي', subtotal: 'المجموع الفرعي', tax: 'الضريبة',
        instructions: 'ملاحظات وتعليمات', thankYou: 'شكراً لتعاملكم معنا'
      } : {
        title: 'PURCHASE ORDER', number: 'NUMBER', date: 'ORDER DATE', delivery: 'DELIVERY DATE',
        supplier: 'SUPPLIER', billTo: 'BILL TO', shipTo: 'SHIP TO', reference: 'REFERENCE',
        description: 'DESCRIPTION', lot: 'LOT #', qty: 'QTY', unitPrice: 'UNIT PRICE',
        discount: 'DISCOUNT', total: 'TOTAL', subtotal: 'SUBTOTAL', tax: 'TAX',
        instructions: 'NOTES & INSTRUCTIONS', thankYou: 'Thank you for your business.'
      };

      const drawFooter = (page: number) => {
        doc.setDrawColor(225, 225, 225);
        doc.line(margin, pageHeight - 13, pageWidth - margin, pageHeight - 13);
        doc.setFont(PDF_UNICODE_FONT, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(125, 125, 125);
        doc.text(`${text(organization?.name, 'Accountix')} • ${labels.title}`, margin, pageHeight - 8, { align });
        doc.text(`${page}`, pageWidth - margin, pageHeight - 8, { align: rightAlign });
      };

      // Clean, repeatable header on every page (the table may span several pages).
      const drawPageHeader = () => {
        doc.setFillColor(20, 20, 20);
        doc.rect(0, 0, pageWidth, 8, 'F');
        doc.setFont(PDF_UNICODE_FONT, 'bold');
        doc.setFontSize(22);
        doc.setTextColor(20, 20, 20);
        doc.text(labels.title, isArabic ? pageWidth - margin : margin, 25, { align });
        doc.setFontSize(9);
        doc.setTextColor(105, 105, 105);
        doc.text(`${labels.number}: ${text(selectedPO.number, '-')}`, isArabic ? pageWidth - margin : margin, 32, { align });
        doc.text(`${labels.date}: ${formatDate(selectedPO.date)}`, isArabic ? pageWidth - margin : margin, 37, { align });
        doc.text(`${labels.delivery}: ${formatDate(selectedPO.deliveryDate)}`, isArabic ? pageWidth - margin : margin, 42, { align });
        doc.setFontSize(11);
        doc.setTextColor(20, 20, 20);
        doc.text(text(organization?.name, 'Your Company'), isArabic ? margin : pageWidth - margin, 25, { align: rightAlign });
        doc.setFontSize(8);
        doc.setTextColor(105, 105, 105);
        const companyLines = doc.splitTextToSize([organization?.address, organization?.phone, organization?.email].filter(Boolean).map(value => text(value)).join(' • '), 72);
        doc.text(companyLines, isArabic ? margin : pageWidth - margin, 31, { align: rightAlign });
      };

      drawPageHeader();
      doc.setFillColor(247, 247, 247);
      doc.roundedRect(margin, 52, contentWidth, 27, 2, 2, 'F');
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      doc.setFontSize(8);
      doc.setTextColor(115, 115, 115);
      doc.text(labels.supplier, isArabic ? pageWidth - margin - 5 : margin + 5, 59, { align });
      doc.setFontSize(11);
      doc.setTextColor(20, 20, 20);
      doc.text(text(supplier?.name || selectedPO.contactName, '-'), isArabic ? pageWidth - margin - 5 : margin + 5, 66, { align });
      doc.setFont(PDF_UNICODE_FONT, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(90, 90, 90);
      const supplierDetails = [supplier?.email || selectedPO.contactEmail, (supplier as Contact & { phone?: string })?.phone, supplier?.address || selectedPO.billingAddress].filter(Boolean).map(value => text(value)).join(' • ');
      doc.text(doc.splitTextToSize(supplierDetails, contentWidth * 0.55), isArabic ? pageWidth - margin - 5 : margin + 5, 72, { align });
      if (selectedPO.reference) {
        doc.setFont(PDF_UNICODE_FONT, 'bold');
        doc.setTextColor(115, 115, 115);
        doc.text(`${labels.reference}:`, isArabic ? margin + 5 : pageWidth - margin - 5, 59, { align: rightAlign });
        doc.setFont(PDF_UNICODE_FONT, 'normal');
        doc.setTextColor(20, 20, 20);
        doc.text(text(selectedPO.reference), isArabic ? margin + 5 : pageWidth - margin - 5, 66, { align: rightAlign });
      }

      const tableData = selectedPO.items.map((item: PurchaseOrderItem) => {
        const subtotal = Number(item.quantity || 0) * Number(item.unitPrice || 0);
        const discountAmount = item.discountType === 'amount' ? Number(item.discount || 0) : subtotal * Number(item.discount || 0) / 100;
        return [
          text(item.description || item.item, '-'), text(item.lotNumber, '-'), text(item.quantity, '0'),
          Number(item.unitPrice || 0).toLocaleString(locale, { minimumFractionDigits: 2 }),
          discountAmount > 0 ? (item.discountType === 'amount' ? money(discountAmount) : `${item.discount}%`) : '-',
          Number(item.total || 0).toLocaleString(locale, { minimumFractionDigits: 2 })
        ];
      });

      autoTable(doc, {
        startY: 88,
        margin: { top: 88, left: margin, right: margin, bottom: 22 },
        head: [[labels.description, labels.lot, labels.qty, labels.unitPrice, labels.discount, labels.total]],
        body: tableData,
        theme: 'grid',
        tableWidth: contentWidth,
        pageBreak: 'auto',
        showHead: 'everyPage',
        headStyles: { fillColor: [20, 20, 20], textColor: 255, font: PDF_UNICODE_FONT, fontStyle: 'bold', fontSize: 8, cellPadding: 3, halign: align },
        bodyStyles: { font: PDF_UNICODE_FONT, fontSize: 8.5, textColor: [35, 35, 35], cellPadding: 3.5, minCellHeight: 9, valign: 'middle' },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: { 0: { cellWidth: 57, halign: align }, 1: { cellWidth: 22, halign: align }, 2: { cellWidth: 16, halign: rightAlign }, 3: { cellWidth: 27, halign: rightAlign }, 4: { cellWidth: 29, halign: rightAlign }, 5: { cellWidth: 30, halign: rightAlign } },
        didDrawPage: (data) => { drawPageHeader(); drawFooter(data.pageNumber); }
      });

      let finalY = ((doc as any).lastAutoTable?.finalY || 88) + 8;
      if (finalY > pageHeight - 72) { doc.addPage(); drawPageHeader(); finalY = 55; }
      const totalsX = isArabic ? margin : pageWidth - margin - 78;
      const totalsWidth = 78;
      doc.setFont(PDF_UNICODE_FONT, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(labels.subtotal, totalsX, finalY, { align });
      doc.text(money(Number(selectedPO.totalAmount || 0) - Number(selectedPO.items.reduce((sum, item) => sum + Number(item.taxAmount || 0), 0))), isArabic ? totalsX + totalsWidth : pageWidth - margin, finalY, { align: rightAlign });
      doc.setDrawColor(220, 220, 220);
      doc.line(totalsX, finalY + 3, isArabic ? totalsX + totalsWidth : pageWidth - margin, finalY + 3);
      finalY += 11;
      const taxTotal = selectedPO.items.reduce((sum, item) => sum + Number(item.taxAmount || 0), 0);
      if (taxTotal > 0) { doc.text(labels.tax, totalsX, finalY, { align }); doc.text(money(taxTotal), isArabic ? totalsX + totalsWidth : pageWidth - margin, finalY, { align: rightAlign }); finalY += 11; }
      doc.setFillColor(20, 20, 20);
      doc.roundedRect(totalsX, finalY - 6, totalsWidth, 16, 2, 2, 'F');
      doc.setFont(PDF_UNICODE_FONT, 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(labels.total, totalsX + 5, finalY + 4, { align });
      doc.text(money(selectedPO.totalAmount), isArabic ? totalsX + totalsWidth - 5 : pageWidth - margin - 5, finalY + 4, { align: rightAlign });
      finalY += 24;
      const instructions = text(selectedPO.deliveryInstructions || selectedPO.notes, labels.thankYou);
      if (finalY > pageHeight - 50) { doc.addPage(); drawPageHeader(); finalY = 55; }
      doc.setFontSize(8);
      doc.setTextColor(115, 115, 115);
      doc.text(labels.instructions, isArabic ? pageWidth - margin : margin, finalY, { align });
      doc.setFont(PDF_UNICODE_FONT, 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(doc.splitTextToSize(instructions, contentWidth), isArabic ? pageWidth - margin : margin, finalY + 6, { align });
      drawFooter(doc.getNumberOfPages());
      doc.save(`${selectedPO.number}_PurchaseOrder.pdf`);
      notify.confirm('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF Generation Error:', err);
      notify.error('Failed to generate PDF');
    }
  };

  const handleReceiveItems = () => {
    if (!selectedPO) return;
    if (hasLocalSession()) {
      notify.confirm('Receiving purchase orders is not available in local mode.');
      return;
    }
    setIsReceiveModalOpen(true);
  };

  const finalizeReceiveItems = async (warehouseId: string, lCost: number = 0) => {
    if (!selectedPO) return;
    if (hasLocalSession()) {
      notify.confirm('Receiving purchase orders is not available in local mode.');
      return;
    }
    try {
      setIsSaving(true);
      
      // Update inventory with landed cost
      await updateInventoryFromPO(selectedPO, selectedPO.id, 'Received', warehouseId, selectedLotNumber, selectedExpiryDate, lCost);

      // Create lot number records if they don't exist
      if (selectedLotNumber) {
        for (const item of selectedPO.items) {
          if (item.itemId) {
            const hasLot = lotNumbers.find(l => l.lotNumber === selectedLotNumber && l.productId === item.itemId);
            if (!hasLot) {
              try {
                await addDoc(collection(db, 'lot_numbers'), {
                  lotNumber: selectedLotNumber,
                  expiryDate: selectedExpiryDate,
                  productId: item.itemId,
                  orgId,
                  createdAt: serverTimestamp()
                });
              } catch (error) {
                console.error("Error creating lot number:", error);
              }
            }
          }
        }
      }

      // Update PO Status
      const receivedEntry = {
        action: 'Items Received',
        by: auth.currentUser?.displayName || 'Unknown',
        byEmail: auth.currentUser?.email || '',
        at: new Date().toISOString(),
        details: `All items marked as received into inventory at warehouse: ${warehouses.find(w => w.id === warehouseId)?.name || stores.find(s => s.id === warehouseId)?.name || warehouseId}.`
      };

      const actionDetails = `All items marked as received into inventory at warehouse: ${warehouses.find(w => w.id === warehouseId)?.name || stores.find(s => s.id === warehouseId)?.name || warehouseId}.`;
      await updateDoc(doc(db, 'documents', selectedPO.id), { 
        status: 'Received',
        isReceived: true,
        receivedAt: serverTimestamp(),
        receivedBy: auth.currentUser?.uid,
        warehouseId: warehouseId,
        updatedAt: serverTimestamp(),
        actionDetails,
        actionLog: arrayUnion(receivedEntry)
      });
      await logHistory(orgId, 'Received Items', actionDetails, auth.currentUser, selectedPO.id, 'PurchaseOrder');

      setSelectedPO({ 
        ...selectedPO, 
        status: 'Received', 
        isReceived: true,
        actionLog: [...(selectedPO.actionLog || []), receivedEntry] 
      });

      notify.confirm(language === 'ar' ? "تم استلام العناصر وتحديث المخزون" : "Items received and inventory updated");
      setIsReceiveModalOpen(false);
    } catch (error) {
      console.error("Error receiving items:", error);
      notify.error("Error updating inventory");
    } finally {
      setIsSaving(false);
    }
  };

  const updateInventoryFromPO = async (po: any, poId: string, type: string = 'Full', warehouseId: string = 'default', lotNo?: string, expiryDate?: string, totalLandedCost: number = 0) => {
    if (!po.items || !Array.isArray(po.items)) return;
    
    const totalLinesAmount = po.items.reduce((sum: number, item: any) => sum + (item.total || 0), 0) || 1;

    for (const item of po.items) {
      if (item.itemId) {
        const itemRef = doc(db, 'items', item.itemId);
        const itemSnap = await getDoc(itemRef);
        
        if (itemSnap.exists()) {
          const itemData = itemSnap.data();
          const quantityToAdd = parseFloat(item.quantity as any) || 0;
          const previousStock = itemData.quantity || 0;
          const newStock = previousStock + quantityToAdd;
          
          // Calculate allocated landed cost for this item
          const lineAmount = item.total || 0;
          const allocatedLandedCost = (lineAmount / totalLinesAmount) * totalLandedCost;
          const landedCostPerUnit = quantityToAdd > 0 ? allocatedLandedCost / quantityToAdd : 0;
          const poUnitPrice = parseFloat(item.unitPrice as any) || 0;
          const newTotalCostPerUnit = poUnitPrice + landedCostPerUnit;

          // Simple weighted average cost update if quantity > 0
          let updatedCostPrice = itemData.costPrice || poUnitPrice;
          if (newStock > 0 && quantityToAdd > 0) {
            updatedCostPrice = ((previousStock * (itemData.costPrice || 0)) + (quantityToAdd * newTotalCostPerUnit)) / newStock;
          }

          const productLogEntry = {
            action: `Inventory Increased (${type})`,
            by: auth.currentUser?.displayName || 'Unknown User',
            byEmail: auth.currentUser?.email || 'No Email',
            at: new Date().toISOString(),
            details: `Qty +${quantityToAdd} @ ${newTotalCostPerUnit.toFixed(2)} (Incl. ${landedCostPerUnit.toFixed(2)} landed) via PO ${po.number}`,
            relatedId: poId,
            relatedType: 'purchase_order'
          };

          // Detailed Stock Update
          const warehouseName = warehouses.find(w => w.id === warehouseId)?.name || stores.find(s => s.id === warehouseId)?.name || 'Main Warehouse';
          let updatedStockRecords = [...(itemData.stockRecords || [])];
          
          updatedStockRecords.push({
            id: Math.random().toString(36).substr(2, 9),
            warehouseId: warehouseId,
            warehouseName: warehouseName,
            quantity: quantityToAdd,
            lotNumber: lotNo || (po as any).reference || po.number,
            expiryDate: expiryDate || '',
            unitCost: newTotalCostPerUnit,
            createdAt: new Date().toISOString()
          });

          await updateDoc(itemRef, {
            quantity: increment(quantityToAdd),
            costPrice: updatedCostPrice,
            [`storeStock.${warehouseId}`]: increment(quantityToAdd),
            stockRecords: updatedStockRecords,
            actionLog: arrayUnion(productLogEntry),
            updatedAt: serverTimestamp()
          });

          // Add stock movement record
          await addDoc(collection(db, 'stock_movements'), {
            itemId: item.itemId,
            storeId: warehouseId,
            type: 'purchase',
            quantity: quantityToAdd,
            previousStock: previousStock,
            newStock: newStock,
            referenceId: poId,
            referenceNumber: po.number,
            createdBy: auth.currentUser?.uid,
            createdByName: auth.currentUser?.displayName || 'Unknown User',
            createdAt: serverTimestamp(),
            orgId: orgId,
            note: `Purchase Order ${po.number}`
          });
        }
      }
    }
  };

  const handleCreateBillSync = async (type: 'Full' | 'Partial') => {
    if (!selectedPO) return;
    if (hasLocalSession()) {
      try {
        const response = await localApiRequest<{ billId?: string }>(
          `/api/organizations/${orgId}/documents/${selectedPO.id}/convert-to-bill`,
          { method: 'POST' }
        );
        const { billId } = localApiRecord<{ billId?: string }>(response);
        const convertedPO = { ...selectedPO, status: 'Converted' as const, ...(billId ? { billId } : {}) };

        setPurchaseOrders(current => current.map(po => po.id === convertedPO.id ? convertedPO : po));
        setSelectedPO(current => current?.id === convertedPO.id ? convertedPO : current);
        setIsModalOpen(false);
        if (onMarkAsBilled) onMarkAsBilled();
        notify.confirm('Purchase order converted to bill');

        if (billId && onNavigate) onNavigate('bills', billId);
      } catch (error) {
        console.error('Local purchase order bill conversion failed:', error);
        notify.error('Failed to convert purchase order to bill');
      }
      return;
    }
    
    if (type === 'Partial') {
      setPartialBillItems((selectedPO.items || []).map(item => ({
        ...item,
        qtyToBillNow: Math.max(0, item.quantity - (item.billedQuantity || 0))
      })));
      setIsPartialBillModalOpen(true);
      return;
    }
    
    try {
      let billNumber = `BILL-${Math.floor(1000 + Math.random() * 9000)}`;
      if (orgSettings?.billPrefix) {
        billNumber = `${orgSettings.billPrefix}${orgSettings.nextBillNumber}`;
        await updateDoc(doc(db, 'settings', orgId), {
          nextBillNumber: (orgSettings.nextBillNumber || 1) + 1
        });
      }

      // Automatically bill remaining quantities
      const itemsToBill = (selectedPO.items || []).map(item => ({
        ...item,
        quantity: Math.max(0, item.quantity - (item.billedQuantity || 0)),
      })).filter(item => item.quantity > 0);

      if (itemsToBill.length === 0) {
         notify.error("This order is already fully billed.");
         return;
      }

      const subtotalAmount = itemsToBill.reduce((sum, item) => sum + ((item.quantity * item.unitPrice) * (1 - (item.discount || 0)/100)), 0);
      const totalTaxAmount = itemsToBill.reduce((sum, item) => sum + (((item.quantity * item.unitPrice) * (1 - (item.discount || 0)/100)) * (taxRates.find(t => t.name === item.taxRate)?.rate ? parseFloat(taxRates.find(t => t.name === item.taxRate)!.rate) / 100 : 0)), 0);
      const totalAmount = subtotalAmount + totalTaxAmount;

      const newBill = {
        type: 'bill',
        contactId: selectedPO.contactId,
        contactName: selectedPO.contactName,
        contactEmail: (selectedPO as any).contactEmail || '',
        reference: selectedPO.number,
        date: new Date().toISOString().split('T')[0],
        dueDate: selectedPO.deliveryDate,
        number: billNumber,
        items: itemsToBill,
        subtotal: subtotalAmount,
        totalAmount: totalAmount,
        currency: selectedPO.currency,
        status: 'Draft',
        brandingTheme: selectedPO.brandingTheme || 'Standard',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        orgId: orgId,
        purchaseOrderId: selectedPO.id,
        actionLog: [{
          action: 'Created',
          user: auth.currentUser?.displayName || 'Unknown',
          email: auth.currentUser?.email || '',
          timestamp: new Date().toISOString(),
          details: `Bill created from Purchase Order ${selectedPO.number} (${type})`
        }]
      };

      const billRef = await addDoc(collection(db, 'documents'), newBill);
      const billActionDetails = `Bill created from Purchase Order ${selectedPO.number} (${type})`;
      await logHistory(orgId, 'Created Bill', billActionDetails, auth.currentUser, billRef.id, 'Bill');
      
      const updatedPOItems = (selectedPO.items || []).map(item => ({
        ...item,
        billedQuantity: item.quantity
      }));

      // Update PO Status
      const billedEntry: ActionLogEntry = {
        action: 'Billed',
        user: auth.currentUser?.displayName || 'Unknown',
        email: auth.currentUser?.email || '',
        timestamp: new Date().toISOString(),
        details: `Purchase Order marked as Billed (${type}). Associated Bill: ${billNumber}`,
        actionLink: billRef.id,
        actionType: 'bill'
      };

      const poUpdateActionDetails = `Purchase Order marked as Billed (${type}). Associated Bill: ${billNumber}`;
      await updateDoc(doc(db, 'documents', selectedPO.id), { 
        items: updatedPOItems,
        status: 'Billed', 
        updatedAt: serverTimestamp(),
        actionDetails: poUpdateActionDetails,
        actionLog: arrayUnion(billedEntry)
      });
      await logHistory(orgId, 'PO Billed', poUpdateActionDetails, auth.currentUser, selectedPO.id, 'Purchase Order');
      
      notify.confirm(`Bill ${billNumber} created successfully`, {
          action: onNavigate ? {
            label: 'View Bill',
            onClick: () => onNavigate('bills', billRef.id)
          } : undefined
      });

      if (onMarkAsBilled) onMarkAsBilled();
      setIsModalOpen(false);
      
      setSelectedPO(prev => prev ? {
        ...prev,
        items: updatedPOItems,
        status: 'Billed'
      } : null);
    } catch (error) {
      console.error(error);
      notify.error('Failed to create Bill');
    }
  };

  const handleConfirmPartialBill = async () => {
    if (!selectedPO) return;
    if (hasLocalSession()) {
      notify.confirm('Bill conversion is not available in local mode.');
      return;
    }
    if (!auth.currentUser) return;
    
    // Validation
    const invalidItem = partialBillItems.find(item => 
      item.qtyToBillNow > (item.quantity - (item.billedQuantity || 0)) || item.qtyToBillNow < 0
    );
    
    if (invalidItem) {
      notify.error(`Invalid quantity for ${invalidItem.item || 'item'}. Cannot exceed remaining quantity.`);
      return;
    }

    const itemsToBill = partialBillItems.filter(item => item.qtyToBillNow > 0);
    if (itemsToBill.length === 0) {
      notify.error("Please specify at least one item quantity to bill.");
      return;
    }

    setIsPartialSaving(true);
    try {
      let billNumber = `BILL-${Math.floor(1000 + Math.random() * 9000)}`;
      if (orgSettings?.billPrefix) {
        billNumber = `${orgSettings.billPrefix}${orgSettings.nextBillNumber}`;
        await updateDoc(doc(db, 'settings', orgId), {
          nextBillNumber: (orgSettings.nextBillNumber || 1) + 1
        });
      }
      
      const billItems = itemsToBill.map(item => ({
        ...item,
        quantity: item.qtyToBillNow,
        total: (item.qtyToBillNow * item.unitPrice) * (1 - (item.discount || 0) / 100)
      }));

      const subtotalAmount = billItems.reduce((sum, item) => sum + item.total, 0);
      const totalTaxAmount = billItems.reduce((sum, item) => sum + (item.total * (taxRates.find(t => t.name === item.taxRate)?.rate ? parseFloat(taxRates.find(t => t.name === item.taxRate)!.rate) / 100 : 0)), 0);
      const totalBillAmount = subtotalAmount + totalTaxAmount;

      const billActionDetails = `Partial Bill ${billNumber} created from PO ${selectedPO.number}`;
      const billActionEntry: ActionLogEntry = {
        action: 'Created',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: billActionDetails
      };

      // Create the Bill
      const billRef = await addDoc(collection(db, 'documents'), {
        number: billNumber,
        contactId: selectedPO.contactId,
        contactName: selectedPO.contactName || 'Supplier',
        contactEmail: (selectedPO as any).contactEmail || '',
        date: new Date().toISOString().split('T')[0],
        dueDate: selectedPO.deliveryDate,
        reference: selectedPO.reference || selectedPO.number,
        currency: selectedPO.currency,
        brandingTheme: selectedPO.brandingTheme || 'Standard',
        totalAmount: totalBillAmount,
        subtotal: subtotalAmount,
        amountsAre: 'Tax Exclusive',
        status: 'Draft',
        items: billItems,
        type: 'bill',
        orgId,
        purchaseOrderId: selectedPO.id,
        actionLog: [billActionEntry],
        createdAt: serverTimestamp(),
      });

      // Update PO Items
      const updatedPOItems = (selectedPO.items || []).map(origItem => {
        const partial = partialBillItems.find(p => p.id === origItem.id || (p.item === origItem.item && p.description === origItem.description));
        if (partial) {
          return {
            ...origItem,
            billedQuantity: (origItem.billedQuantity || 0) + partial.qtyToBillNow
          };
        }
        return origItem;
      });

      const isFullyBilled = updatedPOItems.every(item => (item.billedQuantity || 0) >= item.quantity);
      const newStatus = isFullyBilled ? 'Billed' : 'Approved';

      await updateDoc(doc(db, 'documents', selectedPO.id), {
        items: updatedPOItems,
        status: newStatus,
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion({
          action: 'Partial Bill Created',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || 'No Email',
          timestamp: new Date().toISOString(),
          details: `Created partial bill ${billNumber} for selected items.`,
          actionLink: billRef.id,
          actionType: 'bill'
        })
      });

      notify.confirm(`Partial Bill ${billNumber} created successfully`, {
        action: onNavigate ? {
          label: 'View Bill',
          onClick: () => onNavigate('bills', billRef.id)
        } : undefined
      });
      setIsPartialBillModalOpen(false);
      
      setSelectedPO(prev => prev ? {
        ...prev,
        items: updatedPOItems,
        status: newStatus
      } : null);

    } catch (error) {
      console.error("Error creating partial bill:", error);
      notify.error("Failed to create partial bill");
    } finally {
      setIsPartialSaving(false);
    }
  };

  const updatePurchaseOrderStatus = async (status: 'Sent' | 'Accepted' | 'Rejected') => {
    if (!selectedPO) return;

    const actionDetails = `Purchase Order ${selectedPO.number} ${status === 'Sent' ? 'sent to supplier' : status === 'Accepted' ? 'accepted' : 'rejected'}`;
    const actionEntry: ActionLogEntry = {
      action: status,
      user: auth.currentUser?.displayName || 'Unknown User',
      email: auth.currentUser?.email || '',
      timestamp: new Date().toISOString(),
      details: actionDetails
    };

    try {
      let updatedPO: PurchaseOrder;
      if (hasLocalSession()) {
        const response = await localApiRequest(`/api/organizations/${orgId}/documents/${selectedPO.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status, actionDetails, actionLog: [...(selectedPO.actionLog || []), actionEntry] })
        });
        updatedPO = { ...selectedPO, ...localApiRecord<PurchaseOrder>(response), status };
      } else {
        if (!auth.currentUser) return;
        await updateDoc(doc(db, 'documents', selectedPO.id), {
          status,
          updatedAt: serverTimestamp(),
          actionDetails,
          actionLog: arrayUnion(actionEntry)
        });
        await logHistory(orgId, `${status} Purchase Order`, actionDetails, auth.currentUser, selectedPO.id, 'PurchaseOrder');
        updatedPO = { ...selectedPO, status, actionDetails, actionLog: [...(selectedPO.actionLog || []), actionEntry] };
      }

      updatedPO = normalizePurchaseOrder(updatedPO);
      setPurchaseOrders(current => current.map(po => po.id === updatedPO.id ? updatedPO : po));
      setSelectedPO(updatedPO);
      if (status === 'Rejected') {
        notify.error(language === 'ar' ? 'تم رفض الطلب' : 'Order Rejected');
      } else {
        notify.confirm(language === 'ar' ? (status === 'Sent' ? 'تم الإرسال للمورد' : 'تم قبول الطلب') : (status === 'Sent' ? 'Sent to Supplier' : 'Order Accepted'));
      }
    } catch (error: any) {
      console.error(`Failed to update purchase order status to ${status}:`, error);
      const reason = error?.message && typeof error.message === 'string'
        ? error.message.replace(/^["{]+|[}"]+$/g, '').trim()
        : '';
      const base = language === 'ar' ? 'تعذر تحديث حالة أمر الشراء' : 'Failed to update purchase order status';
      notify.error(reason && /error|denied|404|500|connection|network|ENOTFOUND/i.test(reason) ? `${base}: ${reason}` : base);
    }
  };

  const handleSendEmail = () => {
    if (!selectedPO) return;
    const contact = contacts.find(c => c.id === selectedPO.contactId) || 
                   contacts.find(c => c.name === selectedPO.contactName);
    const email = contact?.email || 
                 (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : null) || 
                 (selectedPO as any).contactEmail;
    
    if (!email) {
      notify.error(language === 'ar' ? "لا يوجد بريد إلكتروني للمورد" : "No email for supplier");
      return;
    }

    const itemsList = selectedPO.items.map(item => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = item.discountType === 'amount' ? item.discount : (subtotal * item.discount) / 100;
      const itemTotal = subtotal - discountAmount;
      return `• ${item.description || item.item} (${item.quantity} x ${selectedPO.currency} ${(item.unitPrice || 0).toLocaleString()}${item.discount ? `, Disc: ${item.discount}${item.discountType === 'percentage' ? '%' : ''}` : ''}): ${selectedPO.currency} ${(itemTotal || 0).toLocaleString()}`;
    }).join('\n');

    const subject = `Purchase Order ${selectedPO.number} from Accountex`;
    const body = `Dear ${contact?.name || selectedPO.contactName || 'Supplier'},

Please find below the details of our Purchase Order ${selectedPO.number}.

Order Date: ${new Date(selectedPO.date).toLocaleDateString()}
Requested Delivery Date: ${new Date(selectedPO.deliveryDate).toLocaleDateString()}
${selectedPO.reference ? `Reference: ${selectedPO.reference}` : ''}

Items Ordered:
${itemsList}

Total Amount: ${selectedPO.currency} ${selectedPO.totalAmount.toLocaleString()}

Please let us know if you have any questions regarding this order.

Best regards,
Accountex Team`;
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };

  const handleSavePO = async (e: React.FormEvent, statusOverride?: PurchaseOrder['status']) => {
    if (e) e.preventDefault();
    if (isEditing && selectedPO && isPurchaseOrderValueLocked(selectedPO.status)) {
      notify.error(language === 'ar' ? 'لا يمكن تعديل قيم أمر شراء مكتمل' : 'Completed purchase order values are locked');
      return;
    }
    const isLocal = hasLocalSession();
    if (!isLocal && !auth.currentUser) return;

    const selectedContact = contacts.find(contact => contact.id === newPO.contactId);
    if (!newPO.contactId || !selectedContact) {
      notify.error(language === 'ar' ? 'يرجى اختيار مورد صالح' : 'Select a valid supplier before saving the purchase order');
      return;
    }
    if (!newPO.number?.trim()) {
      notify.error(language === 'ar' ? 'رقم أمر الشراء مطلوب' : 'Purchase order number is required');
      return;
    }
    if (!newPO.date || !newPO.deliveryDate) {
      notify.error(language === 'ar' ? 'التاريخ وتاريخ التسليم مطلوبان' : 'Order and delivery dates are required');
      return;
    }
    if (!Array.isArray(newPO.items)) {
      notify.error(language === 'ar' ? 'أضف عناصر إلى أمر الشراء' : 'Add at least one item to the purchase order');
      return;
    }
    
    setIsSaving(true);
    const contact = contacts.find(c => c.id === newPO.contactId);
    const contactName = contact?.name || newPO.contactName || '';
    const contactEmail = contact?.email || (newPO as any).contactEmail || '';
    const totalAmount = calculateTotals(newPO.items);
    const finalStatus = statusOverride || newPO.status;
    
    const actionDetails = isEditing 
      ? `Purchase Order ${newPO.number} updated by ${auth.currentUser?.displayName || 'Unknown User'}`
      : `Purchase Order ${newPO.number} created for ${contact?.name || 'Unknown Vendor'}`;

    const actionEntry: ActionLogEntry = {
      action: isEditing ? 'Edited' : (finalStatus === 'Approved' ? 'Approved' : 'Created'),
      user: auth.currentUser?.displayName || 'Unknown User',
      email: auth.currentUser?.email || 'No Email',
      timestamp: new Date().toISOString(),
      details: actionDetails
    };

    try {
      if (isLocal) {
        const localDocument = {
          ...newPO,
          status: finalStatus,
          type: 'purchase_order' as const,
          contactName,
          contactEmail,
          totalAmount,
          actionDetails,
        };
        let response;
        if (isEditing && selectedPO) {
          response = await localApiRequest(`/api/organizations/${orgId}/documents/${selectedPO.id}`, {
            method: 'PATCH',
            body: JSON.stringify(localDocument),
          });
        } else {
          try {
            response = await localApiRequest(`/api/organizations/${orgId}/documents`, {
              method: 'POST',
              body: JSON.stringify(localDocument),
            });
          } catch (createError: any) {
            const isDuplicateNumber = typeof createError?.message === 'string'
              && /document number already exists/i.test(createError.message);
            if (!isDuplicateNumber) throw createError;

            // A tab can keep an old counter while another tab/user creates a PO.
            // Reload the authoritative list and retry once with the next available number.
            const latestDocuments = localApiRecords<PurchaseOrder>(await localApiRequest(
              `/api/organizations/${orgId}/documents?types=purchase_order`
            ));
            const prefix = readPurchaseOrderPrefix(orgSettings);
            const usedNumbers = latestDocuments
              .map(po => String(po.number || ''))
              .filter(number => number.startsWith(prefix))
              .map(number => Number(number.slice(prefix.length)))
              .filter(number => Number.isFinite(number));
            const nextNumber = Math.max(readNextPurchaseOrderNumber(orgSettings) - 1, ...usedNumbers, 0) + 1;
            const recoveredNumber = `${prefix}${nextNumber}`;
            const recoveredDocument = { ...localDocument, number: recoveredNumber };
            setNewPO(current => ({ ...current, number: recoveredNumber }));
            response = await localApiRequest(`/api/organizations/${orgId}/documents`, {
              method: 'POST',
              body: JSON.stringify(recoveredDocument),
            });
          }
        }
        const savedPO = localApiRecord<PurchaseOrder>(response);
        const poWithId = { ...localDocument, ...savedPO, id: savedPO.id || selectedPO?.id } as PurchaseOrder;
        setPurchaseOrders(current => isEditing
          ? current.map(po => po.id === poWithId.id ? poWithId : po)
          : [poWithId, ...current]);
        setSelectedPO(current => current?.id === poWithId.id ? poWithId : current);

        // In local mode the server's documents POST does not advance
        // settings.next_purchase_order_number (unlike the Firestore path,
        // which bumps nextPurchaseOrderNumber below). Mirror that here so
        // the next PO gets a fresh number instead of reusing this one and
        // tripping the unique constraint (409 Document number already exists).
        if (!isEditing) {
          const savedNumber = Number(String(poWithId.number || '').replace(/^[^0-9]*/, ''));
          const nextCounter = Number.isFinite(savedNumber) && savedNumber > 0
            ? savedNumber + 1
            : readNextPurchaseOrderNumber(orgSettings) + 1;
          const updatedSettings = {
            ...orgSettings,
            next_purchase_order_number: nextCounter,
            nextPurchaseOrderNumber: nextCounter,
          };
          setOrgSettings(updatedSettings);
          try {
            await localApiRequest(`/api/organizations/${orgId}/settings`, {
              method: 'PUT',
              body: JSON.stringify({
                ...orgSettings,
                nextPurchaseOrderNumber: nextCounter,
                next_purchase_order_number: nextCounter,
              }),
            });
          } catch (counterError) {
            console.error('Local purchase order counter increment failed:', counterError);
          }
        }
      } else {
        const poData = {
          ...newPO,
          status: finalStatus,
          contactName: contact?.name || '',
          contactEmail: contact?.email || (newPO as any).contactEmail || '',
          totalAmount,
          actionDetails,
          updatedAt: serverTimestamp()
        };

      if (isEditing && selectedPO) {
        await updateDoc(doc(db, 'documents', selectedPO.id), {
          ...poData,
          actionLog: arrayUnion(actionEntry)
        });
        await logHistory(orgId, 'Edited Purchase Order', actionDetails, auth.currentUser, selectedPO.id, 'PurchaseOrder');
      } else {
        const poRef = await addDoc(collection(db, 'documents'), {
          ...poData,
          type: 'purchase_order',
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          orgId,
          createdAt: serverTimestamp(),
          actionLog: [actionEntry]
        });

        await logHistory(orgId, 'Created Purchase Order', actionDetails, auth.currentUser, poRef.id, 'PurchaseOrder');

        // Increment next PO number
        await updateDoc(doc(db, 'settings', orgId), {
          nextPurchaseOrderNumber: increment(1)
        });
      }
      }
      setIsModalOpen(false);
      setIsEditing(false);
      notify.confirm(isEditing ? "Purchase order updated" : (finalStatus === 'Approved' ? "Purchase order approved" : "Purchase order saved as draft"));
    } catch (error: any) {
      if (isLocal) {
        console.error('Local purchase order save failed:', error);
      } else {
        handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'documents');
      }
      const rawMessage = typeof error?.message === 'string' ? error.message : '';
      let apiMessage = rawMessage;
      try {
        const parsed = JSON.parse(rawMessage);
        apiMessage = parsed?.error || parsed?.message || rawMessage;
      } catch {
        // Keep the original message for non-JSON errors such as network failures.
      }
      const fallback = language === 'ar' ? 'تعذر حفظ أمر الشراء' : 'Failed to save purchase order';
      const usefulMessage = apiMessage && !/^request failed \(\d+\)$/i.test(apiMessage)
        ? `${fallback}: ${apiMessage}`
        : fallback;
      notify.error(usefulMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePO = async (id: string) => {
    try {
      if (hasLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/documents/${id}`, { method: 'DELETE' });
        setPurchaseOrders(current => current.filter(po => po.id !== id));
        setSelectedPO(current => current?.id === id ? null : current);
        notify.confirm('Purchase order deleted');
        setDeleteId(null);
        return;
      }
      await updateDoc(doc(db, 'documents', id), {
        status: 'Deleted',
        updatedAt: serverTimestamp()
      });
      notify.confirm("Purchase order deleted");
      setDeleteId(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'documents');
      notify.error("Failed to delete purchase order");
    }
  };

  const filteredPOs = purchaseOrders.filter(po => {
    const tagMatchesSearch = po.tags?.some(tagName => tagName.toLowerCase().includes(searchTerm.toLowerCase())) || false;
    const matchesSearch = po.number.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         normalizeContactName(po.contactName).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.reference.toLowerCase().includes(searchTerm.toLowerCase()) || tagMatchesSearch;
    const matchesStatus = statusFilter === 'All' || po.status === statusFilter;
    
    // Advanced search
    const matchesRef = !refFilter || (po.reference || '').toLowerCase().includes(refFilter.toLowerCase()) || po.number.toLowerCase().includes(refFilter.toLowerCase());
    const contactName = normalizeContactName(po.contactName);
    const matchesCustomer = !customerFilter || contactName.toLowerCase().includes(customerFilter.toLowerCase());
    const matchesTag = !tagFilter || (po.tags && po.tags.includes(tagFilter));

    const matchesMinAmount = !minAmount || (po.totalAmount || 0) >= parseFloat(minAmount);
    const matchesMaxAmount = !maxAmount || (po.totalAmount || 0) <= parseFloat(maxAmount);

    const matchesStartDate = !startDate || po.date >= startDate;
    const matchesEndDate = !endDate || po.date <= endDate;

    return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate && matchesRef && matchesCustomer && matchesTag && matchesMinAmount && matchesMaxAmount;
  }).sort((a, b) => {
    if (sortDirection === 'asc') {
      return a.number.localeCompare(b.number);
    } else if (sortDirection === 'desc') {
      return b.number.localeCompare(a.number);
    }
    return 0;
  });

  const activePOTheme = selectedPO ? brandingThemes.find(t => t.id === selectedPO.brandingTheme) : null;

  const getFilterCount = (status: string) => {
    if (status === 'All') return null;
    return purchaseOrders.filter(po => po.status === status).length;
  };

  return (
    <div className="space-y-6 pt-2 sm:pt-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Orders</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Manage your orders to vendors and suppliers</p>
        </div>
        <button 
          onClick={() => {
            setIsEditing(false);
              setNewPO({
                number: buildPurchaseOrderNumber(orgSettings),
                contactId: '',
                date: new Date().toISOString().split('T')[0],
                deliveryDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                reference: '',
                currency: organizationCurrency,
                brandingTheme: 'Standard',
                amountsAre: 'Tax Exclusive',
                items: [{ 
                  item: '', 
                  description: '', 
                  quantity: 1, 
                  unitPrice: 0,
                  discount: 0,
                  discountType: 'percentage',
                  taxAmount: 0,
                  total: 0,
                  account: '',
                  taxRate: '0%',
                  trackingCategory: ''
                }],
                totalAmount: 0,
                status: 'Draft',
                type: 'purchase_order',
                notes: '',
                deliveryInstructions: '',
                deliveryAddress: '',
                billingAddress: '',
                attachments: [],
                trackingCategory: '',
                tags: []
              });
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          <Plus className="w-4 h-4" />
          <span>New Purchase Order</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col gap-4 bg-white/50 border border-[#141414] p-4 rounded-2xl">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto custom-scrollbar">
            {['All', 'Draft', 'Sent', 'Accepted', 'Rejected', 'Approved', 'Billed', 'Void'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={cn(
                  "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border flex items-center gap-1.5 shrink-0",
                  statusFilter === status 
                    ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                    : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
                )}
              >
                <span>{status}</span>
                {getFilterCount(status) !== null && (
                  <span className={cn(
                    "px-1.5 py-0.5 rounded-full text-[10px]",
                    statusFilter === status ? "bg-white text-[#141414]" : "bg-[#141414]/10 text-[#141414]"
                  )}>
                    {getFilterCount(status)}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full flex-1 md:w-64 shadow-sm focus-within:ring-2 ring-[#141414]/10 transition-all">
                <Search className="w-4 h-4 opacity-50" />
                <input 
                  type="text" 
                  placeholder="Search orders..." 
                  className="bg-transparent border-none outline-none text-sm w-full font-bold"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button 
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={cn(
                  "p-2.5 rounded-full border transition-all shadow-sm",
                  showAdvancedFilters || startDate || endDate || refFilter || customerFilter || tagFilter || minAmount || maxAmount
                    ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                )}
                title="Advanced Filters"
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>
            {/* showDateFilters block removed */}
          </div>
        </div>
      </div>

      {showAdvancedFilters && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-white border border-[#141414]/10 p-6 rounded-3xl shadow-none z-10 animate-in slide-in-from-top-2 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:col-span-4">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
              <input 
                type="text" 
                placeholder="PO-..."
                value={refFilter}
                onChange={e => setRefFilter(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'المورد' : 'Supplier'}</label>
              <input 
                type="text" 
                placeholder={language === 'ar' ? 'اسم المورد...' : 'Supplier...'}
                value={customerFilter}
                onChange={e => setCustomerFilter(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'من' : 'Start Date'}</label>
              <input 
                type="date"
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'إلى' : 'End Date'}</label>
              <input 
                type="date"
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)} placeholder="Min" className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5" />
                <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} placeholder="Max" className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5" />
              </div>
            </div>
            <SearchableSelect 
              label={language === 'ar' ? 'الوسوم' : 'Tags'}
              value={tagFilter}
              onChange={e => setTagFilter(e.target.value)}
              options={[
                { value: '', label: 'All Tags' },
                ...Array.from(new Set(purchaseOrders.flatMap(po => po.tags || []))).map(tag => ({ value: tag, label: tag }))
              ]}
            />
            <div className="md:col-span-2 flex items-end">
              <button 
                onClick={() => {
                  setRefFilter('');
                  setCustomerFilter('');
                  setTagFilter('');
                  setStartDate('');
                  setEndDate('');
                  setMinAmount('');
                  setMaxAmount('');
                  setSearchTerm('');
                }}
                className="px-6 py-2.5 bg-red-50 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-colors shadow-sm ml-auto"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedPOs.length > 0 && (
        <div className="flex items-center gap-4 bg-[#141414] text-[#E4E3E0] p-4 rounded-2xl mb-4 animate-in slide-in-from-top-2 duration-200 dark:bg-[#000000] dark:border dark:border-white/10 dark:text-white">
          <div className="flex items-center gap-2 pr-4 border-r border-white/10">
            <span className="text-xs font-bold italic serif">{selectedPOs.length} selected</span>
            <button 
              onClick={() => setSelectedPOs([])}
              className="text-[10px] uppercase font-bold opacity-50 hover:opacity-100"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-2 md:pb-0">
            <button 
              onClick={() => handleBulkAction('submit')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Send className="w-3 h-3" />
              <span>Submit for Approval</span>
            </button>
            <button 
              onClick={() => handleBulkAction('approve')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <CheckCircle2 className="w-3 h-3" />
              <span>Approve</span>
            </button>
            <button 
              onClick={() => handleBulkAction('delete')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-red-500/20 text-red-400 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Trash2 className="w-3 h-3" />
              <span>Delete</span>
            </button>
            <button 
              onClick={() => handleBulkAction('print')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Printer className="w-3 h-3" />
              <span>Print</span>
            </button>
            <button 
              onClick={() => handleBulkAction('email')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Mail className="w-3 h-3" />
              <span>Email</span>
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading orders...</p>
        </div>
      ) : filteredPOs.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No purchase orders found</p>
          <p className="text-sm opacity-30 mt-2">Create your first order to get started</p>
        </div>
      ) : (
        <div className="bg-white/50 border border-[#141414] rounded-3xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#141414]">
                <th className="p-4 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-[#141414]"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPOs(filteredPOs.map(po => po.id));
                      } else {
                        setSelectedPOs([]);
                      }
                    }}
                    checked={selectedPOs.length === filteredPOs.length && filteredPOs.length > 0}
                  />
                </th>
                <th 
                  className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif cursor-pointer hover:opacity-100 flex items-center gap-1 group"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                >
                  Number
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                    {sortDirection === 'asc' ? '↑' : (sortDirection === 'desc' ? '↓' : '↕')}
                  </span>
                </th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'المرجع' : 'Reference'}</th>
                <th className="p-4 w-10"></th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">To</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Delivery Date</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'الوسوم' : 'Tags'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif text-right">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'الحالة' : 'Status'}</th>
              </tr>
            </thead>
              <tbody className="divide-y divide-[#141414]/5">
                {filteredPOs.map((po) => {
                  return (
                    <tr 
                      key={po.id} 
                      className="hover:bg-[#141414]/5 transition-colors cursor-pointer group border-b border-[#141414]/10"
                      onClick={() => handleSelectPO(po)}
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          className="rounded border-[#141414]"
                          checked={selectedPOs.includes(po.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPOs(prev => [...prev, po.id]);
                            } else {
                              setSelectedPOs(prev => prev.filter(id => id !== po.id));
                            }
                          }}
                        />
                      </td>
                      <td className="p-4 font-mono text-sm font-bold">{po.number}</td>
                      <td className="p-4 text-sm opacity-70 italic serif">{po.reference || ''}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 opacity-20 group-hover:opacity-100 transition-opacity" />
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowThread(po.id);
                            }}
                            className="p-1.5 hover:bg-black/10 group-hover:hover:bg-white/20 rounded-full transition-all opacity-100 relative group-hover:text-[#E4E3E0]"
                            title="Open Thread"
                          >
                            <MessageSquare className="w-4 h-4" />
                            {po.threads?.length > 0 && (
                              <span className="text-xs font-bold text-red-500 ml-1">
                                {po.threads.length}
                              </span>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => {
                            if (onNavigate && (po.contactId)) {
                              onNavigate('contacts', po.contactId);
                            }
                          }}
                          className="font-bold hover:underline hover:text-primary transition-colors focus:outline-none text-left"
                        >
                          {normalizeContactName(po.contactName)}
                        </button>
                      </td>
                      <td className="p-4 text-sm opacity-70">{new Date(po.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className="p-4 text-sm opacity-70">{new Date(po.deliveryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className="p-4">
                        {po.tags && po.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {po.tags.map((tagName, idx) => {
                              const tagDetails = availableTags.find((t: any) => t.name === tagName);
                              return (
                                <span 
                                  key={idx} 
                                  className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full text-[10px] font-bold border"
                                  style={{
                                    backgroundColor: tagDetails ? `${tagDetails.color}15` : '#f3f4f6',
                                    color: tagDetails ? tagDetails.color : '#374151',
                                    borderColor: tagDetails ? `${tagDetails.color}30` : '#e5e7eb'
                                  }}
                                >
                                  <div className="p-0.5 rounded-full bg-white/50">
                                    <TagIcon className="w-2.5 h-2.5" />
                                  </div>
                                  {tagName}
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                      </td>
                      <td className="p-4 font-mono font-bold text-right">
                        {po.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-4 text-right">
                        <StatusBadge status={purchaseOrderBadgeStatus(po.status)} language={language === 'ar' ? 'ar' : 'en'} title={po.status} className="text-[10px] uppercase tracking-wider" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Supplier Modal */}
      {isNewSupplierModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[130] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isCreatingSupplier) setIsNewSupplierModalOpen(false);
          }}
        >
          <form onSubmit={handleCreateSupplier} className="bg-[#E4E3E0] border border-[#141414] w-full max-w-lg rounded-3xl p-8 shadow-2xl space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold italic serif">{language === 'ar' ? 'مورد جديد' : 'New supplier'}</h2>
                <p className="text-sm opacity-50 mt-1">{language === 'ar' ? 'أضف جهة اتصال المورد من أمر الشراء' : 'Add a supplier contact without leaving this purchase order.'}</p>
              </div>
              <button type="button" onClick={() => setIsNewSupplierModalOpen(false)} disabled={isCreatingSupplier} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <input required autoFocus type="text" placeholder="Supplier name *" value={newSupplier.name} onChange={e => setNewSupplier(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-white/60 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative"><Mail className="absolute left-4 top-4 w-4 h-4 opacity-40" /><input type="email" placeholder="Email" value={newSupplier.email} onChange={e => setNewSupplier(prev => ({ ...prev, email: e.target.value }))} className="w-full bg-white/60 border border-[#141414] p-4 pl-11 rounded-2xl outline-none focus:bg-white" /></div>
                <div className="relative"><Phone className="absolute left-4 top-4 w-4 h-4 opacity-40" /><input type="tel" placeholder="Phone" value={newSupplier.phone} onChange={e => setNewSupplier(prev => ({ ...prev, phone: e.target.value }))} className="w-full bg-white/60 border border-[#141414] p-4 pl-11 rounded-2xl outline-none focus:bg-white" /></div>
              </div>
              <div className="relative"><MapPin className="absolute left-4 top-4 w-4 h-4 opacity-40" /><textarea placeholder="Address" value={newSupplier.address} onChange={e => setNewSupplier(prev => ({ ...prev, address: e.target.value }))} className="w-full bg-white/60 border border-[#141414] p-4 pl-11 rounded-2xl outline-none focus:bg-white min-h-24 resize-y" /></div>
            </div>
            <div className="flex gap-3 pt-2 border-t border-[#141414]/10">
              <button type="button" onClick={() => setIsNewSupplierModalOpen(false)} disabled={isCreatingSupplier} className="flex-1 py-3 rounded-full border border-[#141414] font-bold">Cancel</button>
              <button type="submit" disabled={isCreatingSupplier || !newSupplier.name.trim()} className="flex-1 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                {isCreatingSupplier && <Loader2 className="w-4 h-4 animate-spin" />}
                {isCreatingSupplier ? 'Creating...' : 'Create supplier'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Discussion Thread Modal */}
      {showThread && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowThread(null);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-2xl rounded-3xl p-8 shadow-2xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold italic serif">Discussion</h2>
              <button onClick={() => setShowThread(null)} className="p-1 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <DiscussionsView orgId={orgId} documentId={showThread} collectionName="documents" />
            </div>
          </div>
        </div>
      )}

      {/* PO Modal */}
      {isModalOpen && (
        <div
          className="app-overlay-inset ui-editor-overlay fixed inset-y-0 end-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="ui-editor-panel purchase-order-editor bg-[#E4E3E0] w-full max-w-7xl max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col shadow-2xl">
            <div className="ui-editor-header p-4 sm:p-6 border-b border-[#141414]/10 flex items-center justify-between bg-[#141414]/5">
              <h2 className="text-2xl font-bold tracking-tight">
                {isEditing ? `Edit Purchase Order ${newPO.number}` : 'New Purchase Order'}
              </h2>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePO} className="ui-editor-form flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Vendor</label>
                  <SearchableSelect 
                    value={newPO.contactId}
                    onChange={(e) => handleContactChange(e.target.value)}
                    options={contacts.filter(c => c.type === 'Supplier' || c.type === 'Both').map(contact => ({
                      id: contact.id,
                      label: contact.name,
                      subLabel: contact.email
                    }))}
                    placeholder="Select a vendor"
                  />
                  <button
                    type="button"
                    onClick={() => setIsNewSupplierModalOpen(true)}
                    className="flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {language === 'ar' ? 'إضافة مورد جديد' : 'Add new supplier'}
                  </button>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
                  <input 
                    required
                    type="date" 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                    value={newPO.date}
                    onChange={(e) => setNewPO({...newPO, date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Delivery Date</label>
                  <input 
                    required
                    type="date" 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                    value={newPO.deliveryDate}
                    onChange={(e) => setNewPO({...newPO, deliveryDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الوسوم' : 'Tags'}</label>
                <div className="flex gap-2">
                  <SearchableSelect 
                    value={selectedTagToAdd} 
                    onChange={(e) => setSelectedTagToAdd(e.target.value)}
                    options={[
                      { value: '', label: 'Select a tag...' },
                      ...availableTags.filter(t => !newPO.tags?.includes(t.name)).map(tag => ({ value: tag.name, label: tag.name }))
                    ]}
                    className="flex-1"
                  />
                  <button 
                    type="button" 
                    onClick={() => {
                      if (selectedTagToAdd && !newPO.tags?.includes(selectedTagToAdd)) {
                        setNewPO(prev => ({ ...prev, tags: [...(prev.tags || []), selectedTagToAdd] }));
                        setSelectedTagToAdd('');
                      }
                    }}
                    disabled={!selectedTagToAdd}
                    className="px-6 py-4 bg-[#141414] text-[#E4E3E0] rounded-2xl font-bold disabled:opacity-50 transition-opacity"
                  >
                    Add
                  </button>
                </div>
                {availableTags.length === 0 && (
                  <p className="text-[10px] opacity-40 italic font-mono uppercase">No tags set up yet.</p>
                )}
                {newPO.tags && newPO.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newPO.tags.map(tagName => {
                      const tagDetails = availableTags.find((t: any) => t.name === tagName);
                      return (
                        <div key={tagName} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border font-mono" style={{ borderColor: tagDetails?.color || '#141414', backgroundColor: tagDetails?.color ? `${tagDetails.color}20` : 'transparent' }}>
                           <TagIcon className="w-3 h-3" style={{ color: tagDetails?.color || '#141414' }} />
                           {tagName}
                           <button type="button" onClick={() => setNewPO(prev => ({ ...prev, tags: prev.tags?.filter(t => t !== tagName) }))} className="ml-1 opacity-50 hover:opacity-100">
                             <X className="w-3 h-3" />
                           </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'رقم أمر الشراء' : 'PO Number'}</label>
                  <input 
                    required
                    type="text" 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-mono"
                    value={newPO.number}
                    onChange={(e) => setNewPO({...newPO, number: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                  <input 
                    type="text" 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                    placeholder="e.g. Project X"
                    value={newPO.reference}
                    onChange={(e) => setNewPO({...newPO, reference: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'العملة' : 'Currency'}</label>
                  <SearchableSelect 
                    value={newPO.currency}
                    onChange={(e) => setNewPO({...newPO, currency: e.target.value})}
                    options={currencies.map(curr => ({
                      id: curr,
                      label: curr
                    }))}
                    placeholder="Select Currency"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'النسق' : 'Branding Theme'}</label>
                  <SearchableSelect 
                    value={newPO.brandingTheme}
                    onChange={(e) => setNewPO({...newPO, brandingTheme: e.target.value})}
                    options={[
                      { id: 'Standard', label: language === 'ar' ? 'أساسي' : 'Standard' },
                      { id: 'Professional', label: 'Professional' },
                      { id: 'Modern', label: 'Modern' },
                      ...brandingThemes.map(theme => ({ id: theme.id, label: theme.name }))
                    ]}
                    placeholder="Select Theme"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold tracking-tight">{language === 'ar' ? 'عناصر الطلب' : 'Line Items'}</h3>
                  <button 
                    type="button"
                    onClick={handleAddItem}
                    className="flex items-center gap-2 text-xs font-bold hover:opacity-70 transition-opacity"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add New Line</span>
                  </button>
                </div>

                <div className="po-line-items-list space-y-4">
                  {newPO.items.map((item, index) => (
                    <div 
                      key={index} 
                      className="po-line-item-grid grid gap-3 lg:gap-4 items-start bg-white/30 p-3 sm:p-4 rounded-2xl border border-[#141414]/5"
                      style={{ gridTemplateColumns: `repeat(${15 + trackingCategories.length * 2}, minmax(0, 1fr))` }}
                    >
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">SKU / Item</label>
                        <SearchableSelect 
                          className="bg-transparent border-0 p-0 text-sm"
                          value={item.item}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            const selectedItem = itemsList.find(i => i.code === selectedValue || i.name === selectedValue);
                            if (selectedItem) {
                              const newItems = [...newPO.items];
                              newItems[index] = {
                                ...newItems[index],
                                itemId: selectedItem.id,
                                item: selectedItem.code || selectedItem.name,
                                description: selectedItem.description || selectedItem.name,
                                unitPrice: selectedItem.costPrice || selectedItem.unitPrice || 0,
                                uomId: selectedItem.purchaseUomId || selectedItem.stockUomId || '',
                                uomName: selectedItem.purchaseUomName || selectedItem.stockUomName || '',
                                uomCategory: selectedItem.purchaseUomCategory || selectedItem.stockUomCategory || '',
                                account: selectedItem.account || newItems[index].account || '',
                                taxRate: selectedItem.taxRate || newItems[index].taxRate || '0%'
                              };
                              
                              // Recalculate item total for the updated row
                              const item = newItems[index];
                              const subtotal = item.quantity * item.unitPrice;
                              const discountAmount = item.discountType === 'amount'
                                ? item.discount
                                : (subtotal * item.discount) / 100;
                              const taxableAmount = subtotal - discountAmount;
                              const taxRateValue = getTaxRateValue(item.taxRate) / 100;
                              const taxAmount = taxableAmount * taxRateValue;
                              newItems[index].total = taxableAmount + taxAmount;
                              newItems[index].taxAmount = taxAmount;

                              setNewPO({ ...newPO, items: newItems, totalAmount: calculateTotals(newItems) });
                            } else {
                              handleItemChange(index, 'item', selectedValue);
                            }
                          }}
                          options={itemsList.map(i => ({
                            id: i.code || i.name,
                            label: i.name,
                            subLabel: i.code ? `SKU: ${i.code}` : undefined
                          }))}
                          placeholder="Select Item"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الوصف' : 'Description'}</label>
                        <input 
                          required
                          type="text"
                          placeholder={language === 'ar' ? "الوصف" : "Description"}
                          className="w-full bg-transparent border-b border-[#141414]/10 py-1 outline-none focus:border-[#141414] text-sm"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الكمية' : 'Qty'}</label>
                        <input 
                          required
                          type="number"
                          className="w-full bg-transparent border-b border-[#141414]/10 py-1 outline-none focus:border-[#141414] text-sm font-mono"
                          value={isNaN(item.quantity) ? '' : item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">UOM</label>
                        {purchaseUomOptions(itemsList.find(i => i.id === item.itemId || i.code === item.item || i.name === item.item)).length > 0 ? (
                          <select
                            value={item.uomId || item.uomName || ''}
                            onChange={event => {
                              const option = purchaseUomOptions(itemsList.find(i => i.id === item.itemId || i.code === item.item || i.name === item.item))
                                .find(unit => unit.id === event.target.value);
                              const newItems = [...newPO.items];
                              newItems[index] = { ...item, uomId: option?.id || '', uomName: option?.name || '', uomCategory: option?.category || '' };
                              setNewPO({ ...newPO, items: newItems });
                            }}
                            className="w-full bg-transparent border-b border-[#141414]/10 py-1 outline-none text-sm"
                          >
                            {purchaseUomOptions(itemsList.find(i => i.id === item.itemId || i.code === item.item || i.name === item.item)).map(unit => (
                              <option key={unit.id} value={unit.id}>{unit.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="block py-1 text-sm opacity-50">{item.uomName || '—'}</span>
                        )}
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Unit Price</label>
                        <input 
                          required
                          type="number"
                          step="0.01"
                          className="w-full bg-transparent border-b border-[#141414]/10 py-1 outline-none focus:border-[#141414] text-sm font-mono"
                          value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Discount</label>
                        <div className="flex items-center gap-1">
                          <input 
                            type="number"
                            step="0.01"
                            className="flex-1 bg-transparent border-b border-[#141414]/10 py-1 outline-none focus:border-[#141414] text-xs font-mono"
                            placeholder="0.00"
                            value={isNaN(item.discount) ? '' : item.discount}
                            onChange={(e) => handleItemChange(index, 'discount', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                          />
                          <SearchableSelect
                            value={item.discountType || 'percentage'}
                            onChange={(e) => handleItemChange(index, 'discountType', e.target.value)}
                            options={[
                              { value: 'percentage', label: '%' },
                              { value: 'amount', label: 'Amt' }
                            ]}
                            className="w-16"
                          />
                        </div>
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الحساب' : 'Account'}</label>
                        <SearchableSelect 
                          className="bg-transparent border-0 p-0 text-sm"
                          value={item.account}
                          onChange={(val) => handleItemChange(index, 'account', val)}
                          options={accounts.map(acc => ({
                            id: acc.id,
                            label: acc.name,
                            subLabel: acc.code
                          }))}
                          placeholder="Select Account"
                        />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Tax</label>
                        <SearchableSelect 
                          className="bg-transparent border-0 p-0 text-sm"
                          value={item.taxRate}
                          onChange={(val) => handleItemChange(index, 'taxRate', val)}
                          options={[
                            { id: '0%', label: 'Tax Exempt (0%)' },
                            ...taxRates.filter(t => t.type !== 'sales').map(tax => ({
                              id: tax.name,
                              label: tax.name,
                              subLabel: `${tax.rate}%`
                            }))
                          ]}
                          placeholder="Select Tax"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Lot # / Expiry</label>
                        <div className="space-y-1">
                          <SearchableSelect 
                            className="bg-transparent border-0 border-b p-0"
                            value={item.lotNumber || ''}
                            onChange={(e) => {
                              const selectedLotNumber = e.target.value;
                              const product = itemsList.find(i => i.id === item.itemId || i.code === item.item);
                              const lot = product?.lots?.find((l: any) => l.lotNumber === selectedLotNumber);
                              
                              const newItems = [...newPO.items];
                              newItems[index] = { 
                                ...item, 
                                lotNumber: selectedLotNumber,
                                expiryDate: lot?.expiryDate || ''
                              };
                              setNewPO({ ...newPO, items: newItems });
                            }}
                            options={[
                              { value: '', label: 'No Lot' },
                              ...(itemsList.find(i => i.id === item.itemId || i.code === item.item)?.lots?.map((lot: any) => ({
                                value: lot.lotNumber,
                                label: lot.lotNumber
                              })) || [])
                            ]}
                          />
                          {item.expiryDate && (
                            <p className="text-[7px] text-primary/50 font-bold uppercase tracking-tighter leading-none">Exp: {item.expiryDate}</p>
                          )}
                        </div>
                      </div>
                      {trackingCategories.map(cat => (
                        <div key={cat.id} className="col-span-2 space-y-1">
                          <label className="text-[8px] uppercase font-bold opacity-40">{cat.name}</label>
                          <SearchableSelect 
                            className="bg-transparent border-0 border-b p-0"
                            value={item.trackingCategory?.split('|').find(p => p.startsWith(`${cat.id}:`))?.split(':')[1] || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const currentParts = item.trackingCategory ? item.trackingCategory.split('|') : [];
                              const otherParts = currentParts.filter(p => !p.startsWith(`${cat.id}:`));
                              const newParts = val ? [...otherParts, `${cat.id}:${val}`] : otherParts;
                              handleItemChange(index, 'trackingCategory', newParts.join('|'));
                            }}
                            options={[
                              { value: '', label: 'None' },
                              ...cat.options.map((opt: string) => ({ value: opt, label: opt }))
                            ]}
                          />
                        </div>
                      ))}
                      <div className="col-span-1 space-y-1 text-right">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الإجمالي' : 'Total'}</label>
                        <div className="py-1 text-sm font-mono font-bold">
                          {item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="col-span-1 pt-4 flex justify-end">
                        <button 
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1 hover:text-red-500 transition-colors"
                          disabled={newPO.items.length === 1}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col lg:flex-row justify-between gap-6 lg:gap-8 pt-6 lg:pt-8 border-t border-[#141414]/10">
                <div className="flex-1 space-y-6 min-w-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Delivery Address</label>
                      <textarea 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all h-24 text-sm"
                        placeholder="Enter delivery address..."
                        value={newPO.deliveryAddress}
                        onChange={(e) => setNewPO({...newPO, deliveryAddress: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Delivery Instructions</label>
                      <textarea 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all h-24 text-sm"
                        placeholder="Enter delivery instructions..."
                        value={newPO.deliveryInstructions}
                        onChange={(e) => setNewPO({...newPO, deliveryInstructions: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50 block">Attachments</label>
                    <div className="flex flex-wrap gap-4">
                      {newPO.attachments?.map((url, idx) => (
                        <div key={idx} className="relative group">
                          <div className="w-20 h-20 bg-white border border-[#141414] rounded-xl flex items-center justify-center overflow-hidden">
                            <FileText className="w-8 h-8 opacity-20" />
                          </div>
                          <button 
                            type="button"
                            onClick={() => setNewPO(prev => ({ ...prev, attachments: prev.attachments?.filter((_, i) => i !== idx) }))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <label className="w-20 h-20 bg-white/50 border-2 border-dashed border-[#141414]/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#141414] transition-all">
                        <Plus className="w-6 h-6 opacity-30" />
                        <span className="text-[8px] font-bold uppercase opacity-40 mt-1">File</span>
                        <input type="file" className="hidden" onChange={handleFileUpload} />
                      </label>
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-80 lg:shrink-0 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold">Total (Excl. Disc)</span>
                    <span className="font-mono">{newPO.currency} {newPO.items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  {newPO.items.reduce((sum, i) => {
                    const base = i.quantity * i.unitPrice;
                    const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                    return sum + disc;
                  }, 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold text-red-500">Less: Discount</span>
                      <span className="font-mono text-red-500">-{newPO.currency} {newPO.items.reduce((sum, i) => {
                        const base = i.quantity * i.unitPrice;
                        const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                        return sum + disc;
                      }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t border-[#141414]/10">
                    <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                    <span className="font-mono">{newPO.currency} {(newPO.items.reduce((sum, i) => {
                      const base = i.quantity * i.unitPrice;
                      const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                      return sum + (base - disc);
                    }, 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold">Tax Total</span>
                    <span className="font-mono">{newPO.currency} {newPO.items.reduce((sum, i) => sum + i.taxAmount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-[#141414]">
                    <span className="italic serif text-[#0073AA]">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                    <span className="font-mono text-[#0073AA]">{newPO.currency} {newPO.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              <div className="ui-editor-actions">
                <button 
                  type="button"
                  data-editor-secondary="true"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button 
                  type="submit"
                  data-editor-primary="true"
                  disabled={isSaving}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-30"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isEditing ? (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes') : (language === 'ar' ? 'حفظ الصنف' : 'Save')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PO Detail View */}
      {/* PO Detail View */}
      {isReceiveModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsReceiveModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold italic serif">Receive Items</h2>
              <button 
                onClick={() => setIsReceiveModalOpen(false)}
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="p-4 bg-white border border-[#141414] rounded-2xl space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest opacity-40">Purchase Order</p>
                <p className="font-bold">{selectedPO?.number}</p>
                <p className="text-xs opacity-60">{selectedPO?.contactName}</p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-3 opacity-50 flex items-center gap-2">
                  <Store className="w-3 h-3" />
                  Select Delivery Warehouse
                </label>
                <div className="space-y-2">
                  {(warehouses.length > 0 || stores.length > 0) ? (
                    <SearchableSelect
                      value={selectedWarehouseId}
                      onChange={(e) => setSelectedWarehouseId(e.target.value)}
                      options={[
                        ...warehouses.map(w => ({ value: w.id, label: w.name, group: 'Warehouses' })),
                        ...stores.map(store => ({ value: store.id, label: store.name, group: 'Stores' }))
                      ]}
                      className="w-full"
                    />
                  ) : (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-xs text-red-600 font-bold flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      No warehouses defined. Items will be received into the default warehouse.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-3 opacity-50 flex items-center gap-2">
                  Lot Selection & Expiry
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <SearchableSelect
                      value={selectedLotNumber}
                      onChange={(e) => handleLotChange(e.target.value)}
                      options={[
                        { value: '', label: 'New Lot / PO Number' },
                        ...lotNumbers.map(l => ({ value: l.lotNumber, label: l.lotNumber }))
                      ]}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <input 
                      type="date"
                      value={selectedExpiryDate}
                      onChange={(e) => setSelectedExpiryDate(e.target.value)}
                      className="w-full bg-white border border-[#141414] p-3 rounded-2xl outline-none font-bold text-xs"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-3 opacity-50 flex items-center gap-2">
                  Landed Cost (Shipping/Fees)
                </label>
                <input 
                  type="number"
                  value={landedCost}
                  onChange={(e) => setLandedCost(e.target.value)}
                  placeholder="Enter total extra costs to distribute..."
                  className="w-full bg-white border border-[#141414] p-3 rounded-2xl outline-none font-bold text-xs shadow-sm focus:ring-1 ring-primary/20"
                />
                <p className="text-[9px] opacity-40 mt-1 italic uppercase tracking-wider">Cost will be distributed among items based on their line total value.</p>
              </div>

              <div className="flex gap-4 pt-4 border-t border-[#141414]">
                <button 
                  onClick={() => setIsReceiveModalOpen(false)}
                  className="flex-1 py-4 rounded-full border border-[#141414] font-bold hover:bg-[#141414]/5 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  disabled={isSaving}
                  onClick={() => finalizeReceiveItems(selectedWarehouseId, parseFloat(landedCost) || 0)}
                  className="flex-1 py-4 rounded-full bg-[#141414] text-[#E4E3E0] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
                  Confirm Delivery
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPO && !isModalOpen && (
        <div className="app-overlay-inset fixed inset-y-0 end-0 bg-[#E4E3E0] z-[100] flex flex-col overflow-hidden print-only print:inset-0">
          {/* Top Header / Breadcrumbs */}
          <div className="bg-[#E4E3E0] border-b border-[#141414]/10 px-8 py-4 print:hidden">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-[11px] text-[#0073AA] font-bold">
                  <button onClick={handleClosePO} className="hover:underline tracking-widest uppercase">Purchases</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 text-[#141414]/40" />
                  <button onClick={handleClosePO} className="hover:underline tracking-widest uppercase">Purchase Orders</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 text-[#141414]/40" />
                  <span className="tracking-widest uppercase text-[#141414]/60">{selectedPO.number}</span>
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-[#141414] tracking-tight italic serif">Purchase Order {selectedPO.number}</h2>
                  <StatusBadge status={purchaseOrderBadgeStatus(selectedPO.status)} language={language === 'ar' ? 'ar' : 'en'} title={selectedPO.status} className="text-xs uppercase tracking-widest" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selectedPO.status === 'Draft' && (
                  <button 
                    onClick={() => void updatePurchaseOrderStatus('Sent')}
                    className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    <Send className="w-3 h-3" />
                    <span>{language === 'ar' ? 'إرسال للمورد' : 'Send to Supplier'}</span>
                  </button>
                )}
                {selectedPO.status === 'Sent' && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => void updatePurchaseOrderStatus('Accepted')}
                      className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-green-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      <span>{language === 'ar' ? 'قبول' : 'Accept'}</span>
                    </button>
                    <button 
                      onClick={() => void updatePurchaseOrderStatus('Rejected')}
                      className="flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-red-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <XCircle className="w-3 h-3" />
                      <span>{language === 'ar' ? 'رفض' : 'Reject'}</span>
                    </button>
                  </div>
                )}
                {selectedPO.status === 'Accepted' && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={handleReceiveItems}
                      disabled={isSaving}
                      className="flex items-center gap-2 bg-teal-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-teal-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageCheck className="w-3 h-3" />}
                      <span>{language === 'ar' ? 'استلام المواد' : 'Receive Items'}</span>
                    </button>
                    <button 
                      onClick={() => {
                        // For now we'll implement the "Full Bill" logic directly
                        // We can add a modal for partial billing if needed
                        handleCreateBillSync('Full');
                      }}
                      className="flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-indigo-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <FileText className="w-3 h-3" />
                      <span>{language === 'ar' ? 'فاتورة كاملة' : 'Full Bill'}</span>
                    </button>
                    <button 
                      onClick={() => {
                        notify.confirm("Partial billing coming soon. Opening full bill for now.");
                        handleCreateBillSync('Partial');
                      }}
                      className="flex items-center gap-2 bg-indigo-400 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-indigo-500 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <FileText className="w-3 h-3" />
                      <span>{language === 'ar' ? 'فاتورة جزئية' : 'Partial Bill'}</span>
                    </button>
                  </div>
                )}
                {selectedPO.status === 'Received' && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleCreateBillSync('Full')}
                      className="flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-indigo-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <FileText className="w-3 h-3" />
                      <span>{language === 'ar' ? 'إنشاء فاتورة' : 'Create Bill'}</span>
                    </button>
                  </div>
                )}
                {(selectedPO.status === 'Awaiting Approval' || selectedPO.status === 'Approved') && (
                  // Support legacy statuses if they exist: let the buyer mark the
                  // order as Accepted so they can receive items and create bills.
                  <button
                    onClick={() => void updatePurchaseOrderStatus('Accepted')}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-indigo-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    Process Order
                  </button>
                )}
                {(selectedPO.status === 'Approved' || selectedPO.status === 'Billed') && (
                  <button 
                    onClick={() => {
                      setLandedCostLines(selectedPO.landedCostLines || [{ id: Date.now().toString(), expenseName: '', amount: 0 }]);
                      setLandedCostMethod(selectedPO.landedCostMethod || 'value');
                      setPreviewAllocations(null);
                      setIsLandedCostModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    <TagIcon className="w-3 h-3" />
                    Landed Costs
                  </button>
                )}

                {!isPurchaseOrderValueLocked(selectedPO.status) && (
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setNewPO({ ...selectedPO });
                      setIsModalOpen(true);
                    }}
                    className="p-2 text-[#141414]/40 hover:text-[#141414] transition-colors"
                    title="Edit Status / Content"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                )}
                <button onClick={() => setShareMode('link')} className="p-2 text-[#141414]/40 hover:text-[#141414]" title="Get Link"><LinkIcon className="w-5 h-5" /></button>
                <button onClick={() => setShareMode('qr')} className="p-2 text-[#141414]/40 hover:text-[#141414]" title="QR Code"><QrCode className="w-5 h-5" /></button>
                <button className="text-[#141414]/40 hover:text-[#141414] transition-colors hover:scale-110 active:scale-95" onClick={() => handlePrint(selectedPO)}>
                  <Printer className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleDownloadPDF}
                  className="p-2 text-[#141414]/40 hover:text-[#141414] transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-5 h-5" />
                </button>

                <button 
                  onClick={handleClosePO}
                  className="ml-4 p-1 hover:bg-[#141414]/5 rounded-full transition-colors"
                >
                  <XCircle className="w-6 h-6 text-[#141414]/40 hover:text-[#141414]" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-8 md:p-16 custom-scrollbar text-[#141414] print:overflow-visible print:p-0 print:h-auto">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Main Card */}
              <div className="bg-[#E4E3E0] border border-[#141414]/20 rounded-2xl shadow-xl overflow-hidden min-h-[600px]">
                {/* Metadata Grid */}
                <div className="p-8 grid grid-cols-1 md:grid-cols-6 gap-8 bg-white/30">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Supplier</p>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <p 
                          onClick={() => {
                            if (onNavigate && selectedPO.contactId) {
                               onNavigate('suppliers', selectedPO.contactId);
                            }
                          }}
                          className="text-sm font-bold text-[#141414] hover:underline cursor-pointer italic serif"
                        >
                          {contacts.find(c => c.id === selectedPO.contactId)?.name || normalizeContactName(selectedPO.contactName) || 'Walk-in Contact'}
                        </p>
                        <button 
                          onClick={handleSendEmail}
                          className="p-1 hover:bg-black/5 rounded-full transition-all text-primary/40 hover:text-primary"
                          title="Send via Email"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-[10px] text-[#141414]/60 font-medium">
                        <div className="flex items-center gap-2">
                          <p className="text-xs opacity-60 font-bold">
                            {(() => {
                              const contact = contacts.find(c => c.id === selectedPO.contactId) || 
                                             contacts.find(c => c.name === selectedPO.contactName);
                              const email = contact?.email || (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : null) || (selectedPO as any).contactEmail;
                              return email || 'No email provided';
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'التاريخ' : 'Date'}</p>
                    <p className="text-sm text-[#141414] font-bold">{new Date(selectedPO.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Delivery Date</p>
                    <p className="text-sm text-[#141414] font-bold">{new Date(selectedPO.deliveryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'رقم أمر الشراء' : 'PO Number'}</p>
                    <p className="text-sm text-[#141414] font-mono font-bold">{selectedPO.number}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'العملة' : 'Currency'}</p>
                    <p className="text-sm text-[#141414] font-bold">{selectedPO.currency}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Theme</p>
                    <p className="text-sm text-[#141414] font-bold">{selectedPO.brandingTheme || 'Standard'}</p>
                  </div>
                </div>

                <div className="px-8 py-4 bg-white/10 flex justify-between">
                  <p className="text-[10px] text-[#141414]/40 font-bold uppercase tracking-widest italic">{selectedPO.reference ? `Ref: ${selectedPO.reference}` : ''}</p>
                </div>

                {/* Items Table */}
                <div className="border-t border-[#141414]/10">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-white/20 border-b border-[#141414]/10">
                        <th className="py-4 px-8 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Item</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">UOM</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">{language === 'ar' ? 'السعر' : 'Price'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Tax</th>
                        <th className="py-4 px-8 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#141414]/5">
                      {selectedPO.items.map((item, idx) => {
                        const itemData = itemsList.find(i => i.id === item.item || i.code === item.item);
                        return (
                          <tr key={idx} className="hover:bg-white/40 transition-colors">
                            <td className="py-5 px-8 text-sm font-bold text-[#141414]">
                              <button 
                                onClick={() => {
                                  if (onNavigate && (itemData?.id || item.item)) {
                                    onNavigate('inventory', itemData?.id || item.item);
                                  }
                                }}
                                className="text-left hover:underline hover:text-[#141414] transition-colors focus:outline-none"
                              >
                                {itemData?.code || item.item} {itemData?.name ? `- ${itemData.name}` : ''}
                              </button>
                            </td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] italic serif">{item.description}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{(item.quantity || 0).toFixed(2)}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414]">{item.uomName || itemData?.purchaseUomName || itemData?.stockUomName || '—'}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{(item.discount && item.discount > 0) ? ((item as any).discountType === 'amount' ? item.discount.toLocaleString() : `${item.discount}%`) : '-'}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{item.taxAmount > 0 ? item.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                            <td className="py-5 px-8 text-sm font-bold text-[#141414] text-right">
                              {(() => {
                                const subtotalBeforeDiscount = item.quantity * item.unitPrice;
                                const discountAmount = (item as any).discountType === 'amount' 
                                  ? (item.discount || 0) 
                                  : (subtotalBeforeDiscount * (item.discount || 0) / 100);
                                const subtotal = subtotalBeforeDiscount - discountAmount;
                                return (subtotal + (item.taxAmount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Totals Section */}
                <div className="p-8 flex flex-col md:flex-row justify-between items-start gap-12 border-t border-[#141414]/10 bg-white/10">
                  <div className="space-y-6 flex-1 max-w-md">
                    {selectedPO.deliveryAddress && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-2">Delivery Address</p>
                        <p className="text-xs opacity-80 font-bold whitespace-pre-wrap">{selectedPO.deliveryAddress}</p>
                      </div>
                    )}
                    {selectedPO.deliveryInstructions && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-2">Delivery Instructions</p>
                        <p className="text-xs opacity-60 leading-relaxed whitespace-pre-wrap">{selectedPO.deliveryInstructions}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="w-80 space-y-4">
                    <div className="flex justify-between items-center text-xs font-bold text-[#141414]/60 uppercase tracking-widest">
                      <span>Total (Excl. Disc)</span>
                      <span className="font-mono">{selectedPO.currency} {selectedPO.items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    {selectedPO.items.reduce((sum, i) => {
                      const base = i.quantity * i.unitPrice;
                      const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                      return sum + disc;
                    }, 0) > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-[#141414]/60 uppercase tracking-widest">
                        <span className="text-red-500">Less: Discount</span>
                        <span className="font-mono text-red-500">-{selectedPO.currency} {selectedPO.items.reduce((sum, i) => {
                          const base = i.quantity * i.unitPrice;
                          const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                          return sum + disc;
                        }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs font-bold text-[#141414]/60 uppercase tracking-widest pt-2 border-t border-[#141414]/10">
                      <span>{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                      <span className="font-mono">{selectedPO.currency} {(selectedPO.items.reduce((sum, i) => {
                        const base = i.quantity * i.unitPrice;
                        const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                        return sum + (base - disc);
                      }, 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    {selectedPO.items.reduce((sum, i) => sum + (i.taxAmount || 0), 0) > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-[#141414]/60 uppercase tracking-widest">
                        <span>{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                        <span className="font-mono">{selectedPO.currency} {selectedPO.items.reduce((sum, i) => sum + (i.taxAmount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-2xl font-bold pt-4 border-t-2 border-[#141414]">
                      <span className="italic serif">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                      <span className="font-mono text-[#0073AA]">
                        {selectedPO.currency} {selectedPO.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedPO.landedCostLines && selectedPO.landedCostLines.length > 0 && selectedPO.landedCostTotal && (
                   <div className="p-8 border-t border-[#141414]/10 bg-blue-50/30 auto-rows-auto">
                     <h3 className="text-sm font-bold uppercase tracking-widest text-blue-800 mb-4 flex items-center gap-2">
                       <TagIcon className="w-4 h-4" /> Allocated Landed Costs (True Cost)
                     </h3>
                     <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs font-mono">
                          <thead>
                            <tr className="border-b border-blue-900/10 text-blue-900/60 uppercase tracking-widest">
                               <th className="py-2 px-4">Item</th>
                               <th className="py-2 px-4 text-right">Base Total</th>
                               <th className="py-2 px-4 text-right">Landed Freight/Duty</th>
                               <th className="py-2 px-4 text-right font-bold text-blue-800">True Cost (Total)</th>
                               <th className="py-2 px-4 text-right font-bold bg-green-50">Adj. Unit Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPO.items.map((item, idx) => (
                               <tr key={idx} className="border-b border-blue-900/5 group">
                                 <td className="py-3 px-4 font-sans font-bold">{item.item}</td>
                                 <td className="py-3 px-4 text-right">{(item.unitPrice * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                 <td className="py-3 px-4 text-right text-blue-600">+{item.allocatedLandedCost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                 <td className="py-3 px-4 text-right font-bold text-blue-800">
                                    {((item.unitPrice * item.quantity) + (item.allocatedLandedCost || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                 </td>
                                 <td className="py-3 px-4 text-right font-bold bg-green-50 text-green-800 group-hover:bg-green-100 transition-colors">
                                    {item.weightedUnitCost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                 </td>
                               </tr>
                            ))}
                          </tbody>
                        </table>
                     </div>
                   </div>
                )}
              </div>

              {/* Action Log Box */}
              {selectedPO.actionLog && selectedPO.actionLog.length > 0 && (
                <div className="bg-[#E4E3E0] border border-[#141414]/20 rounded-2xl shadow-xl overflow-hidden mt-8 print:hidden">
                  <div className="flex items-center justify-between border-b border-[#141414]/10 p-6 bg-white/30">
                    <h3 className="text-xl font-bold italic serif">Action Log</h3>
                    <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">
                      {selectedPO.actionLog.length} Event{selectedPO.actionLog.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-[#141414]/10 bg-white/10">
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">Action By</th>
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">Action Type</th>
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">Changes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#141414]/5">
                        {[...selectedPO.actionLog].reverse().map((entry, idx) => (
                          <tr key={idx} className="text-xs hover:bg-white/40 transition-colors">
                            <td className="py-4 px-6 opacity-60 whitespace-nowrap">{new Date(entry.timestamp).toLocaleString()}</td>
                            <td className="py-4 px-6">
                              <div className="flex flex-col">
                                <span className="font-bold whitespace-nowrap">{entry.user}</span>
                                <span className="opacity-40 text-[10px]">{entry.email}</span>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="px-2 py-1 bg-[#141414]/5 rounded-full font-bold uppercase tracking-tighter text-[10px]">
                                {entry.action}
                              </span>
                            </td>
                            <td className="py-4 px-6 leading-relaxed max-w-md font-medium">
                              {entry.relatedId && entry.relatedType === 'bill' ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onNavigate) onNavigate('bills', entry.relatedId);
                                  }}
                                  className="text-[#0073AA] hover:underline flex items-center gap-1 group/link"
                                >
                                  {entry.details}
                                  <ArrowRight className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                </button>
                              ) : (
                                <span className="opacity-60">{entry.details}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <DocumentChatterPanel
                orgId={orgId}
                entityId={selectedPO.id}
                entityType="PurchaseOrder"
                locale={language === 'ar' ? 'ar' : 'en'}
                className="mt-8 print:hidden"
                defaultCollapsed
              />
            </div>
          </div>
        </div>
      )}

      {shareMode && selectedPO && <ShareDocumentModal document={selectedPO} type="purchase-order" mode={shareMode} onClose={() => setShareMode(null)} />}

      {/* Partial Bill Modal */}
      {isPartialBillModalOpen && selectedPO && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between bg-white">
              <div>
                <h2 className="text-xl font-bold italic serif text-primary">{language === 'ar' ? 'إنشاء فاتورة جزئية' : 'Create Partial Bill'}</h2>
                <p className="text-[10px] font-bold text-primary/40 uppercase tracking-widest">{selectedPO.number}</p>
              </div>
              <button onClick={() => setIsPartialBillModalOpen(false)} className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors">
                <X className="w-5 h-5 opacity-50" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-white rounded-xl border border-border overflow-hidden shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-widest text-primary/40 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 font-bold">{language === 'ar' ? 'الصنف' : 'Item'}</th>
                      <th className="px-6 py-4 font-bold text-right">{language === 'ar' ? 'الكمية المطلوبة' : 'Ordered Qty'}</th>
                      <th className="px-6 py-4 font-bold text-right text-emerald-600">{language === 'ar' ? 'الكمية المفوترة مسبقاً' : 'Previously Billed'}</th>
                      <th className="px-6 py-4 font-bold text-right text-orange-600">{language === 'ar' ? 'الكمية المراد فوترتها الآن' : 'Qty to Bill Now'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {partialBillItems.map((item, idx) => {
                      const previouslyBilled = item.billedQuantity || 0;
                      const remainingQty = Math.max(0, item.quantity - previouslyBilled);
                      
                      return (
                        <tr key={idx} className="hover:bg-[#F8F9FA] transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-bold text-primary">{item.item}</div>
                            {item.description && <div className="text-[11px] text-primary/60">{item.description}</div>}
                          </td>
                          <td className="px-6 py-4 text-right font-mono text-primary/70">{item.quantity}</td>
                          <td className="px-6 py-4 text-right font-mono text-emerald-600">{previouslyBilled}</td>
                          <td className="px-6 py-4 text-right">
                            <input
                              type="number"
                              min="0"
                              max={remainingQty}
                              value={item.qtyToBillNow !== undefined ? item.qtyToBillNow : remainingQty}
                              onChange={(e) => {
                                const newItems = [...partialBillItems];
                                newItems[idx].qtyToBillNow = Math.min(remainingQty, Math.max(0, parseInt(e.target.value) || 0));
                                setPartialBillItems(newItems);
                              }}
                              className="w-24 border border-border rounded-lg px-3 py-1.5 text-right font-mono text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-orange-600"
                            />
                            {remainingQty === 0 && <span className="ml-2 text-[10px] text-amber-600 font-bold bg-amber-50 px-2 py-0.5 rounded">Billed</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-6 border-t border-[#141414]/10 bg-white flex justify-end gap-3 flex-shrink-0">
              <button 
                onClick={() => setIsPartialBillModalOpen(false)}
                className="px-6 py-3 rounded-xl font-bold hover:bg-[#141414]/5 transition-colors text-primary"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button 
                disabled={isPartialSaving}
                onClick={handleConfirmPartialBill}
                className="px-8 py-3 rounded-xl font-bold bg-primary text-primary-foreground disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all active:scale-95"
              >
                {isPartialSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                {language === 'ar' ? 'تأكيد إنشاء الفاتورة الجزئية' : 'Create Partial Bill'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Landed Cost Wizard Modal */}
      {isLandedCostModalOpen && selectedPO && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between bg-white">
              <h2 className="text-xl font-bold italic serif">Allocate Landed Costs</h2>
              <button onClick={() => setIsLandedCostModalOpen(false)} className="p-2 hover:bg-[#141414]/5 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-[#141414]/10 space-y-4 shadow-sm">
                  <h3 className="font-bold border-b pb-2">1. Expense Lines</h3>
                  {landedCostLines.map((line, idx) => (
                    <div key={line.id} className="flex items-center gap-3">
                      <input 
                        type="text" 
                        placeholder="Cost Type (e.g. Freight)" 
                        value={line.expenseName}
                        onChange={e => {
                          const newLines = [...landedCostLines];
                          newLines[idx].expenseName = e.target.value;
                          setLandedCostLines(newLines);
                        }}
                        className="flex-1 bg-transparent border-b border-[#141414]/20 focus:border-[#141414] outline-none py-2 text-sm"
                      />
                      <input 
                        type="number" 
                        placeholder={language === 'ar' ? "المبلغ" : "Amount"} 
                        value={line.amount || ''}
                        onChange={e => {
                          const newLines = [...landedCostLines];
                          newLines[idx].amount = parseFloat(e.target.value) || 0;
                          setLandedCostLines(newLines);
                        }}
                        className="w-32 bg-transparent border-b border-[#141414]/20 focus:border-[#141414] outline-none py-2 text-sm font-mono"
                      />
                      <button 
                        onClick={() => setLandedCostLines(landedCostLines.filter((_, i) => i !== idx))}
                        className="p-2 hover:text-red-500 opacity-50 hover:opacity-100 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button 
                    onClick={() => setLandedCostLines([...landedCostLines, { id: Date.now().toString(), expenseName: '', amount: 0 }])}
                    className="text-xs font-bold uppercase tracking-widest text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add Expense
                  </button>
                </div>

                <div className="bg-white p-6 rounded-xl border border-[#141414]/10 space-y-4 shadow-sm">
                  <h3 className="font-bold border-b pb-2">2. Allocation Method</h3>
                  <p className="text-xs opacity-60">Choose how to distribute the total selected expenses across the PO items.</p>
                  
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border border-[#141414]/10 rounded-lg cursor-pointer hover:bg-[#141414]/5">
                      <input 
                        type="radio" 
                        name="allocationMethod" 
                        value="value"
                        checked={landedCostMethod === 'value'}
                        onChange={() => setLandedCostMethod('value')}
                        className="w-4 h-4 accent-[#141414]"
                      />
                      <div className="flex-1">
                        <div className="font-bold text-sm">By Value</div>
                        <div className="text-[10px] opacity-60">(Line Price / Total PO Price) * Expense</div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border border-[#141414]/10 rounded-lg cursor-pointer hover:bg-[#141414]/5">
                      <input 
                        type="radio" 
                        name="allocationMethod" 
                        value="quantity"
                        checked={landedCostMethod === 'quantity'}
                        onChange={() => setLandedCostMethod('quantity')}
                        className="w-4 h-4 accent-[#141414]"
                      />
                      <div className="flex-1">
                        <div className="font-bold text-sm">By Quantity</div>
                        <div className="text-[10px] opacity-60">(Line Qty / Total PO Qty) * Expense</div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border border-[#141414]/10 rounded-lg cursor-pointer hover:bg-[#141414]/5">
                      <input 
                        type="radio" 
                        name="allocationMethod" 
                        value="weight"
                        checked={landedCostMethod === 'weight'}
                        onChange={() => setLandedCostMethod('weight')}
                        className="w-4 h-4 accent-[#141414]"
                      />
                      <div className="flex-1">
                        <div className="font-bold text-sm">By Weight/Volume</div>
                        <div className="text-[10px] opacity-60">Based on product weight fields</div>
                      </div>
                    </label>
                  </div>

                  <button 
                    onClick={calculateLandedCosts}
                    className="w-full bg-[#141414] text-white py-3 rounded-lg font-bold uppercase tracking-widest text-xs hover:opacity-90 mt-4 active:scale-95 transition-all"
                  >
                    Calculate Preview
                  </button>
                </div>
              </div>

              {previewAllocations && (
                <div className="bg-white rounded-xl border border-[#141414]/10 shadow-sm overflow-hidden mt-6">
                  <div className="px-6 py-4 border-b font-bold bg-[#141414]/5 flex justify-between">
                    <span>3. Allocation Preview</span>
                    <span className="font-mono text-[#0073AA]">Total Expense Applied: {selectedPO.currency} {landedCostLines.reduce((s, l) => s + (l.amount||0), 0).toLocaleString()}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-white text-[10px] uppercase tracking-widest opacity-60 border-b border-[#141414]/10">
                        <tr>
                          <th className="px-6 py-3">Item</th>
                          <th className="px-6 py-3 text-right">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                          <th className="px-6 py-3 text-right">Base Unit Cost</th>
                          <th className="px-6 py-3 text-right text-blue-600 bg-blue-50/50">Landed Allocation</th>
                          <th className="px-6 py-3 text-right bg-green-50/50">New Weighted Unit Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#141414]/10 font-mono text-sm">
                        {previewAllocations.map((item, idx) => (
                          <tr key={idx} className="hover:bg-[#141414]/5">
                            <td className="px-6 py-4 font-sans font-bold">{item.item}</td>
                            <td className="px-6 py-4 text-right">{item.quantity}</td>
                            <td className="px-6 py-4 text-right">{(item.unitPrice || 0).toLocaleString(undefined, {minimumFractionDigits:2})}</td>
                            <td className="px-6 py-4 text-right text-blue-600 bg-blue-50/50 font-bold">
                              +{item.allocatedLandedCost?.toLocaleString(undefined, {minimumFractionDigits:2})}
                            </td>
                            <td className="px-6 py-4 text-right bg-green-50/50 font-bold">
                              {item.weightedUnitCost?.toLocaleString(undefined, {minimumFractionDigits:2})}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[#141414]/10 bg-white flex justify-end gap-3">
              <button 
                onClick={() => setIsLandedCostModalOpen(false)}
                className="px-6 py-3 rounded-xl font-bold hover:bg-[#141414]/5 transition-colors"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button 
                disabled={!previewAllocations}
                onClick={handleApplyLandedCosts}
                className="px-8 py-3 rounded-xl font-bold bg-[#141414] text-white disabled:opacity-30 hover:opacity-90 active:scale-95 transition-all"
              >
                Apply Landed Cost
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDeletePO(deleteId)}
        title="Delete Purchase Order"
        message="Are you sure you want to delete this purchase order? This action cannot be undone."
        confirmText="Delete"
      />
      {/* Print Modal */}
      {isPrintModalOpen && docToPrint && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[250] flex flex-col items-center overflow-auto p-8 print:p-0 print:bg-white print:block">
          <div className="flex flex-col gap-6 w-full max-w-[850px] print:w-full print:max-w-none">
            <div className="flex items-center justify-between bg-white/80 backdrop-blur-lg p-4 rounded-2xl border border-primary/10 shadow-lg print:hidden">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight">Print Preview</h3>
                  <p className="text-[10px] opacity-50 font-bold uppercase tracking-widest">{docToPrint.number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold text-xs hover:opacity-90 transition-all shadow-md active:scale-95"
                >
                  <Printer className="w-4 h-4" />
                  Print Now
                </button>
                <button 
                  onClick={() => setIsPrintModalOpen(false)}
                  className="p-2.5 hover:bg-red-50 text-red-600 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow-2xl rounded-sm overflow-hidden print:shadow-none print:rounded-none">
              <DocumentPrintLayout 
                organization={organization}
                document={docToPrint}
                contact={contacts.find(c => c.id === docToPrint.contactId)}
                type="purchase_order"
                language={language}
              />
            </div>
            
            <div className="flex justify-center pb-12 print:hidden">
               <p className="text-[10px] items-center gap-2 flex font-bold uppercase tracking-widest opacity-40">
                  <span className="w-2 h-2 bg-primary/20 rounded-full animate-pulse" />
                  Standard Odoo-Matching Print Layout
               </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
